// frontend/src/pages/Admin.jsx
import { useState, useEffect, useCallback } from 'react';
import ClientMenuSwitches from './ClientMenuSwitches';
import { useLocation } from 'react-router-dom';
import { useOrderAlerts } from '../context/OrderAlertsContext';
import { adminAPI, investmentAPI, exchangeAPI, cryptoWithdrawAPI, priceAPI, configAPI, gatewayAPI, ispPaymentAPI, withdrawalAPI, merchantAPI, investmentProjectAPI, featuresAPI, feeAPI, cryptoDepositAPI, cryptoNetworksAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  Card, Button, Badge, StatCard, Modal, Input, Select,
  Spinner, Empty, Table, Td, SectionHeader,
} from '../components/ui';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { IspPackagesAdminTab, IspVoucherStockAdminTab, IspActivationsAdminTab } from './ISP';
import { LaunchEventAdminTab } from './EventRegistration';
import { PartnershipAdminTab } from './Partnership';
import { NetworkAdminTab } from './Network';
import { MarketAdminTab } from './MarketAdmin';
import { RefreshCw, Search, ShieldCheck, ShieldX, Zap, Send, Copy, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

// Mirrors services/withdrawalStuckChecker.js's STUCK_THRESHOLDS (1h/6h/24h)
// with frontend-only display metadata. Not fetched from the API — these are
// fixed display buckets, not configurable state.
const STUCK_LEVELS = [
  { level: '1h',  label: 'Stuck 1h+',  color: 'text-cyan',   badgeColor: 'cyan' },
  { level: '6h',  label: 'Stuck 6h+',  color: 'text-yellow', badgeColor: 'yellow' },
  { level: '24h', label: 'Stuck 24h+', color: 'text-red',    badgeColor: 'red' },
];

export default function Admin() {
  const { isAdmin } = useAuth();

  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'dashboard');
  // Follow ?tab= when the address changes while Admin is open (e.g. "Open" on a new-order banner).
  const { search: locSearch } = useLocation();
  useEffect(() => { const t = new URLSearchParams(locSearch).get('tab'); if (t) setTab(t); }, [locSearch]);
  const { count: newOrders } = useOrderAlerts();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [kycQueue, setKycQueue] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Crypto withdrawals
  const [cryptoWithdrawals, setCryptoWithdrawals]       = useState([]);
  const [cryptoFilter, setCryptoFilter]                 = useState('');
  const [cryptoLoading, setCryptoLoading]               = useState(false);
  const [cryptoMarkSentModal, setCryptoMarkSentModal]   = useState(null);
  const [cryptoMarkSentTxHash, setCryptoMarkSentTxHash] = useState('');
  const [cryptoRejectModal, setCryptoRejectModal]       = useState(null);
  const [cryptoRejectReason, setCryptoRejectReason]     = useState('');
  const [cryptoTreasury, setCryptoTreasury]             = useState(null);
  // Reconciliation + mock mode controls (additive)
  const [cryptoReconcile, setCryptoReconcile]           = useState(null);
  const [reconcileLoading, setReconcileLoading]         = useState(false);
  const [mockModeConfig, setMockModeConfig]             = useState(null);
  const [mockModeToggling, setMockModeToggling]         = useState(false);

  // Payment Gateway Registry
  const [gwProviders, setGwProviders]       = useState([]);
  const [gwHealth, setGwHealth]             = useState([]);
  const [gwLoading, setGwLoading]           = useState(false);
  const [gwHealthLoading, setGwHealthLoading] = useState(false);
  const [gwAddModal, setGwAddModal]         = useState(false);
  const [gwForm, setGwForm]                 = useState({ name: '', displayName: '', country: '', currency: 'UGX', priority: 10, depositEnabled: true, payoutEnabled: true, active: true, credentialMode: 'env', credentialKeys: '', description: '', webhookUrl: '' });
  const [gwAdapters, setGwAdapters]         = useState([]);

  // ISP Payments (Phase 14)
  const [ispPayments, setIspPayments]         = useState([]);
  const [ispLoading, setIspLoading]           = useState(false);
  const [ispFilter, setIspFilter]             = useState('');
  const [ispMethodFilter, setIspMethodFilter] = useState('');
  const [ispDetailModal, setIspDetailModal]   = useState(null);
  const [ispPage, setIspPage]                 = useState(1);
  const [ispSubTab, setIspSubTab]             = useState('payments');
  const [ispTotal, setIspTotal]               = useState(0);

  // Withdrawals (Phase 16)
  const [withdrawals, setWithdrawals]           = useState([]);
  const [wdStats, setWdStats]                   = useState(null);
  const [wdLoading, setWdLoading]               = useState(false);
  const [wdStatusFilter, setWdStatusFilter]     = useState('');
  const [wdMethodFilter, setWdMethodFilter]     = useState('');
  const [wdFlaggedFilter, setWdFlaggedFilter]   = useState('');
  const [wdPage, setWdPage]                     = useState(1);
  const [wdTotal, setWdTotal]                   = useState(0);
  const [wdDetailModal, setWdDetailModal]       = useState(null);
  const [wdRejectModal, setWdRejectModal]       = useState(null);
  const [wdCancelModal, setWdCancelModal]       = useState(null);
  const [wdActionReason, setWdActionReason]     = useState('');
  const [wdActionLoading, setWdActionLoading]   = useState(false);

  // Stuck withdrawals (DEBT-005 Stage 1) — read-only detection/display, no
  // separate filters/pagination like the main queue above: this list is
  // expected to be small (ideally zero) and every row needs eyes on it.
  const [stuckWithdrawals, setStuckWithdrawals] = useState([]);
  const [stuckLoading, setStuckLoading]         = useState(false);

  // Merchants (Phase 17)
  const [merchants, setMerchants]               = useState([]);
  const [merchantLoading, setMerchantLoading]   = useState(false);
  // Phase 20 ? Feature Flags
  const [adminFeatures, setAdminFeatures]             = useState({});
  const [feeRules, setFeeRules]                       = useState([]);
  const [feeRulesLoading, setFeeRulesLoading]         = useState(false);
  const [feeEditModal, setFeeEditModal]               = useState(false);
  const [feeEditRule, setFeeEditRule]                 = useState(null);
  const [feeEditForm, setFeeEditForm]                 = useState({});
  const [cryptoNets, setCryptoNets]                     = useState([]);
  const [cryptoNetsLoading, setCryptoNetsLoading]       = useState(false);
  const [cryptoNetModal, setCryptoNetModal]             = useState(false);
  const [cryptoNetEdit, setCryptoNetEdit]               = useState(null);
  const [cryptoNetForm, setCryptoNetForm]               = useState({ code:'', name:'', currency:'', symbol:'', minDeposit:10, depositFee:0, withdrawalFee:1, confirmationsRequired:20, explorerTxUrl:'', explorerAddressUrl:'', notes:'' });
  const [cryptoWallets, setCryptoWallets]               = useState([]);
  const [cryptoWalletNet, setCryptoWalletNet]           = useState(null);
  const [cryptoWalletModal, setCryptoWalletModal]       = useState(false);
  const [cryptoWalletForm, setCryptoWalletForm]         = useState({ address:'', label:'Primary Wallet', walletType:'hot' });
  const [cryptoDeposits, setCryptoDeposits]             = useState([]);
  const [cryptoDepositsLoading, setCryptoDepositsLoading] = useState(false);
  const [cryptoDepDetail, setCryptoDepDetail]           = useState(null);
  const [cryptoDepModal, setCryptoDepModal]             = useState(false);
  const [cryptoDepCreditAmount, setCryptoDepCreditAmount] = useState('');
  const [cryptoDepRejectReason, setCryptoDepRejectReason] = useState('');
  const [cryptoDepMoreInfoNote, setCryptoDepMoreInfoNote] = useState('');
  const [cryptoActionLoading, setCryptoActionLoading]   = useState(false);
  // Phase 27 — Exchange Settings
  const [exSettings, setExSettings]   = useState([]);
  const [exLoading, setExLoading]     = useState(false);
  const [exEditing, setExEditing]     = useState({});
  const [exSaving, setExSaving]       = useState({});
  const [deskState, setDeskState]     = useState(null);
  const [deskLoading, setDeskLoading] = useState(false);
  const [deskToggling, setDeskToggling] = useState({}); // { buying: bool, selling: bool }
  const [adminFeaturesLoading, setAdminFeaturesLoading] = useState(false);
  // Phase 19 ? Investment Projects
  const [iProjects, setIProjects]               = useState([]);
  const [iProjectsLoading, setIProjectsLoading] = useState(false);
  const [iProjForm, setIProjForm]               = useState({ name:'', tagline:'', category:'internet', description:'', currency:'UGX', fundingTarget:'', minimumInvestment:'', totalShares:'', sharePrice:'', expectedAnnualROIPercent:'', expectedPayoutCycle:'quarterly', status:'fundraising', isPublished: false });
  const [iProjCreateLoading, setIProjCreateLoading] = useState(false);
  const [merchantFilter, setMerchantFilter]     = useState('');
  const [merchantDetail, setMerchantDetail]     = useState(null);
  const [merchantPayments, setMerchantPayments] = useState([]);
  const [registerModal, setRegisterModal]       = useState(false);
  const [regForm, setRegForm]                   = useState({ userId: '', businessName: '', businessType: 'retail', phone: '', email: '', settlementFrequency: 'on_demand', paymentFeePercent: 1.5 });
  const [regLoading, setRegLoading]             = useState(false);
  const [regUserQuery, setRegUserQuery]         = useState('');
  const [regUserResults, setRegUserResults]     = useState([]);
  const [regUserSearching, setRegUserSearching] = useState(false);
  const [regSelectedUser, setRegSelectedUser]   = useState(null);

  // Pricing engine (Phase 11)
  const [priceData, setPriceData]         = useState(null);
  const [priceLoading, setPriceLoading]   = useState(false);
  const [priceConfigs, setPriceConfigs]   = useState([]);
  const [priceEditing, setPriceEditing]   = useState({});
  const [priceSaving, setPriceSaving]     = useState({});
  const [priceRefreshing, setPriceRefreshing] = useState(false);

  // Exchange queue
  const [exchangeQueue, setExchangeQueue] = useState([]);
  const [exchangeFilter, setExchangeFilter] = useState('');
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [markSentModal, setMarkSentModal] = useState(null);
  const [markSentTxHash, setMarkSentTxHash] = useState('');
  const [rejectExchangeModal, setRejectExchangeModal] = useState(null);
  const [rejectExchangeReason, setRejectExchangeReason] = useState('');

  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);

  const [txFilter, setTxFilter] = useState('');
  const [txStatus, setTxStatus] = useState('');
  const [txPage, setTxPage] = useState(1);
  const [txTotal, setTxTotal] = useState(0);

  // Reject modal
  const [rejectModal, setRejectModal]           = useState(null);
  const [rejectReason, setRejectReason]           = useState('');
  const [rejectReasonOther, setRejectReasonOther] = useState('');
  const KYC_REJECT_REASONS = [
    'ID image blurry or unreadable',
    'Selfie does not match ID',
    'Document expired',
    'Document appears tampered',
    'Document type not accepted',
    'Selfie not submitted',
    'Back of ID required',
    'Other (specify below)',
  ];

  // Suspend modal
  const [suspendModal, setSuspendModal] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');

  // Reset password modal (help-desk tool)
  const [resetPwModal, setResetPwModal] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, kycRes, txRes, fundsRes] = await Promise.allSettled([
        adminAPI.getDashboard(),
        adminAPI.getUsers({ page: userPage, limit: 20, search: userSearch }),
        adminAPI.getKYCQueue({ status: 'pending' }),
        adminAPI.getTransactions({ page: txPage, limit: 20, type: txFilter || undefined, status: txStatus || undefined }),
        adminAPI.getInvestmentReport(),
      ]);

      if (statsRes.status === 'fulfilled')  setStats(statsRes.value.stats || null);
      if (usersRes.status === 'fulfilled')  { setUsers(usersRes.value.users || []); setUserTotal(usersRes.value.pagination?.total || 0); }
      if (kycRes.status === 'fulfilled')    setKycQueue(kycRes.value.users || []);
      if (txRes.status === 'fulfilled')     { setTransactions(txRes.value.transactions || []); setTxTotal(txRes.value.pagination?.total || 0); }
      if (fundsRes.status === 'fulfilled')  setFunds(fundsRes.value.funds || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [userPage, userSearch, txPage, txFilter, txStatus]);

  const fetchExchangeQueue = useCallback(async () => {
    setExchangeLoading(true);
    try {
      const params = exchangeFilter ? { status: exchangeFilter } : {};
      const res = await exchangeAPI.getAllOrders(params);
      setExchangeQueue(res.orders || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load exchange queue');
    } finally {
      setExchangeLoading(false);
    }
  }, [exchangeFilter]);

  const fetchCryptoWithdrawals = useCallback(async () => {
    setCryptoLoading(true);
    try {
      const params = cryptoFilter ? { status: cryptoFilter } : {};
      const [ordersRes, treasuryRes, mockCfgRes] = await Promise.allSettled([
        cryptoWithdrawAPI.getAllOrders(params),
        cryptoWithdrawAPI.getTreasury(),
        configAPI.get('CRYPTO_MOCK_MODE'),
      ]);
      if (ordersRes.status   === 'fulfilled') setCryptoWithdrawals(ordersRes.value.withdrawals || []);
      if (treasuryRes.status === 'fulfilled') setCryptoTreasury(treasuryRes.value.stats || null);
      if (mockCfgRes.status  === 'fulfilled') setMockModeConfig(mockCfgRes.value.config || mockCfgRes.value);
    } catch (err) {
      toast.error(err.message || 'Failed to load crypto withdrawals');
    } finally {
      setCryptoLoading(false);
    }
  }, [cryptoFilter]);

  const fetchPricing = useCallback(async (silent = false) => {
    if (!silent) setPriceLoading(true);
    try {
      const [priceRes, cfgRes] = await Promise.allSettled([
        priceAPI.getUsdt(),
        configAPI.getAll('pricing'),
      ]);
      if (priceRes.status === 'fulfilled') setPriceData(priceRes.value.price);
      if (cfgRes.status  === 'fulfilled') {
        const configs = cfgRes.value.configs || cfgRes.value.data || [];
        setPriceConfigs(configs);
        // Seed editing state from live values
        const init = {};
        configs.forEach(c => { init[c.key] = String(c.value); });
        setPriceEditing(init);
      }
    } catch { /* silent */ }
    finally { if (!silent) setPriceLoading(false); }
  }, []);

  const handlePriceConfigSave = async (key) => {
    setPriceSaving(s => ({ ...s, [key]: true }));
    try {
      const cfg = priceConfigs.find(c => c.key === key);
      let value = priceEditing[key];
      if (cfg?.type === 'number')  value = parseFloat(value);
      if (cfg?.type === 'boolean') value = value === 'true' || value === true;
      await configAPI.set(key, value);
      toast.success(`${key} updated`);
      fetchPricing(true);
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setPriceSaving(s => ({ ...s, [key]: false }));
    }
  };

  const handlePriceRefresh = async () => {
    setPriceRefreshing(true);
    try {
      await priceAPI.refreshCache();
      toast.success('Price cache cleared — next request will fetch fresh data from CoinGecko');
      fetchPricing(true);
    } catch (err) {
      toast.error(err.message || 'Refresh failed');
    } finally {
      setPriceRefreshing(false);
    }
  };

  const fetchGateway = useCallback(async () => {
    setGwLoading(true);
    try {
      const [provRes, adpRes] = await Promise.allSettled([
        gatewayAPI.listProviders(),
        gatewayAPI.listAdapters(),
      ]);
      if (provRes.status === 'fulfilled') setGwProviders(provRes.value.providers || []);
      if (adpRes.status  === 'fulfilled') setGwAdapters(adpRes.value.adapters   || []);
    } catch (err) { toast.error(err.message || 'Failed to load gateway providers'); }
    finally  { setGwLoading(false); }
  }, []);

  const fetchGwHealth = useCallback(async () => {
    setGwHealthLoading(true);
    try {
      const res = await gatewayAPI.healthCheck();
      setGwHealth(res.providers || []);
    } catch (err) { toast.error(err.message || 'Health check failed'); }
    finally  { setGwHealthLoading(false); }
  }, []);

  const fetchIspPayments = useCallback(async () => {
    setIspLoading(true);
    try {
      const params = { page: ispPage, limit: 50 };
      if (ispFilter)       params.status = ispFilter;
      if (ispMethodFilter) params.method = ispMethodFilter;
      const res = await ispPaymentAPI.getAllPayments(params);
      setIspPayments(res.payments || []);
      setIspTotal(res.total || 0);
    } catch (err) { toast.error(err.message || 'Failed to load ISP payments'); }
    finally  { setIspLoading(false); }
  }, [ispFilter, ispMethodFilter, ispPage]);

  const handleIspRetryCallback = async (id) => {
    try {
      await ispPaymentAPI.retryCallback(id);
      toast.success('Callback retry sent');
      fetchIspPayments();
    } catch (err) { toast.error(err.message || 'Retry failed'); }
  };

  const handleIspViewDetail = async (id) => {
    try {
      const res = await ispPaymentAPI.getPaymentById(id);
      setIspDetailModal(res.payment);
    } catch (err) { toast.error(err.message || 'Failed to load detail'); }
  };

  const fetchWithdrawals = useCallback(async () => {
    setWdLoading(true);
    try {
      const params = { page: wdPage, limit: 20 };
      if (wdStatusFilter)  params.status  = wdStatusFilter;
      if (wdMethodFilter)  params.method  = wdMethodFilter;
      if (wdFlaggedFilter) params.flagged = wdFlaggedFilter;
      const [listRes, statsRes] = await Promise.allSettled([
        withdrawalAPI.getAll(params),
        withdrawalAPI.getStats(),
      ]);
      if (listRes.status === 'fulfilled') {
        setWithdrawals(listRes.value.withdrawals || []);
        setWdTotal(listRes.value.total || 0);
      }
      if (statsRes.status === 'fulfilled') setWdStats(statsRes.value.data || null);
    } catch (err) { toast.error(err.message || 'Failed to load withdrawals'); }
    finally  { setWdLoading(false); }
  }, [wdPage, wdStatusFilter, wdMethodFilter, wdFlaggedFilter]);

  const fetchStuckWithdrawals = useCallback(async () => {
    setStuckLoading(true);
    try {
      const res = await withdrawalAPI.getStuck();
      setStuckWithdrawals(res.stuck || []);
    } catch (err) { toast.error(err.message || 'Failed to load stuck withdrawals'); }
    finally  { setStuckLoading(false); }
  }, []);

  const handleWdAction = async (action, id, reason) => {
    setWdActionLoading(true);
    try {
      const fn = {
        approve: () => withdrawalAPI.approve(id),
        process: () => withdrawalAPI.process(id),
        reject:  () => withdrawalAPI.reject(id, reason),
        retry:   () => withdrawalAPI.retry(id),
        cancel:  () => withdrawalAPI.cancel(id, reason),
      }[action];
      await fn();
      toast.success(`Withdrawal ${action}d`);
      setWdRejectModal(null); setWdCancelModal(null); setWdActionReason('');
      fetchWithdrawals();
    } catch (err) { toast.error(err.message || `${action} failed`); }
    finally  { setWdActionLoading(false); }
  };

  const handleWdViewDetail = async (id) => {
    try {
      const res = await withdrawalAPI.getAdminById(id);
      setWdDetailModal(res.data);
    } catch (err) { toast.error(err.message || 'Failed to load detail'); }
  };

  const handleGwAdd = async () => {
    try {
      const data = {
        ...gwForm,
        credentialKeys: gwForm.credentialKeys.split(',').map(s => s.trim()).filter(Boolean),
        priority: parseInt(gwForm.priority) || 10,
      };
      await gatewayAPI.registerProvider(data);
      toast.success(`Provider "${data.displayName}" registered`);
      setGwAddModal(false);
      setGwForm({ name: '', displayName: '', country: '', currency: 'UGX', priority: 10, depositEnabled: true, payoutEnabled: true, active: true, credentialMode: 'env', credentialKeys: '', description: '', webhookUrl: '' });
      fetchGateway();
    } catch (err) { toast.error(err.message || 'Failed to register provider'); }
  };

  const handleGwToggle = async (p) => {
    try {
      await gatewayAPI.updateProvider(p._id, { active: !p.active });
      toast.success(`${p.displayName} ${p.active ? 'deactivated' : 'activated'}`);
      fetchGateway();
    } catch (err) { toast.error(err.message || 'Update failed'); }
  };

  const fetchAdminFeatures = async () => {
    setAdminFeaturesLoading(true);
    try {
      const res = await featuresAPI.list();
      setAdminFeatures(res.features || {});
    } catch (err) { toast.error(err.message || 'Failed to load feature flags'); }
    finally { setAdminFeaturesLoading(false); }
  };

  const fetchFeeRules = useCallback(async () => {
    setFeeRulesLoading(true);
    try {
      const res = await feeAPI.getAll();
      setFeeRules(res.rules || []);
    } catch (err) { toast.error(err.message || 'Failed to load fee rules'); }
    finally { setFeeRulesLoading(false); }
  }, []);

  const fetchCryptoNets = async () => {
    setCryptoNetsLoading(true);
    try {
      const r = await cryptoNetworksAPI.getAll();
      setCryptoNets(r.networks || []);
    } catch (err) { toast.error(err.message || 'Failed to load crypto networks'); }
    finally { setCryptoNetsLoading(false); }
  };

  const fetchCryptoNetsWallets = async (netCode) => {
    try {
      const r = await cryptoNetworksAPI.getWallets(netCode);
      setCryptoWallets(r.wallets || []);
    } catch (err) { toast.error(err.message || 'Failed to load wallets'); }
  };

  const fetchCryptoDeposits = async () => {
    setCryptoDepositsLoading(true);
    try {
      const r = await cryptoDepositAPI.getAll({ limit: 100 });
      setCryptoDeposits(r.deposits || []);
    } catch (err) { toast.error(err.message || 'Failed to load crypto deposits'); }
    finally { setCryptoDepositsLoading(false); }
  };

  const fetchExSettings = async () => {
    setExLoading(true);
    try {
      const r = await exchangeAPI.getSettings();
      const entries = r.settings || [];
      setExSettings(entries);
      const init = {};
      entries.forEach(e => { init[e.key] = String(e.value ?? ''); });
      setExEditing(init);
    } catch (err) { toast.error(err.message || 'Failed to load exchange settings'); }
    finally { setExLoading(false); }
  };

  const fetchDeskState = async () => {
    setDeskLoading(true);
    try {
      const r = await exchangeAPI.getDeskState();
      setDeskState(r.desk);
    } catch (err) { toast.error(err.message || 'Failed to load desk state'); }
    finally { setDeskLoading(false); }
  };

  const toggleDesk = async (direction, nextEnabled) => {
    setDeskToggling(p => ({ ...p, [direction]: true }));
    try {
      const r = await exchangeAPI.setDeskDirection(direction, nextEnabled);
      setDeskState(r.desk);
      toast.success(`${direction === 'buying' ? 'Buying USDT' : 'Selling USDT'} turned ${nextEnabled ? 'ON' : 'OFF'}`);
    } catch (err) { toast.error(err.message || 'Failed to update desk state'); }
    finally { setDeskToggling(p => ({ ...p, [direction]: false })); }
  };

  const updateAdminFeature = async (key, visibility) => {
    try {
      await featuresAPI.update(key, { visibility });
      toast.success('Feature updated: ' + key + ' -> ' + visibility);
      fetchAdminFeatures();
    } catch (err) { toast.error(err.message || 'Update failed'); }
  };

  const fetchIProjects = async () => {
    setIProjectsLoading(true);
    try {
      const res = await investmentProjectAPI.adminList();
      setIProjects(res.projects || []);
    } catch (err) { toast.error(err.message || 'Failed to load investment projects'); }
    finally { setIProjectsLoading(false); }
  };

  const handleCreateIProject = async () => {
    setIProjCreateLoading(true);
    try {
      await investmentProjectAPI.create({
        ...iProjForm,
        fundingTarget:             parseFloat(iProjForm.fundingTarget) || 0,
        minimumInvestment:         parseFloat(iProjForm.minimumInvestment) || 0,
        totalShares:               parseInt(iProjForm.totalShares) || 0,
        sharePrice:                parseFloat(iProjForm.sharePrice) || 0,
        expectedAnnualROIPercent:  parseFloat(iProjForm.expectedAnnualROIPercent) || 0,
      });
      toast.success('Project created!');
      fetchIProjects();
    } catch (err) { toast.error(err.message || 'Failed to create project'); }
    finally { setIProjCreateLoading(false); }
  };

  const toggleIProjectPublished = async (proj) => {
    try {
      await investmentProjectAPI.update(proj._id, { isPublished: !proj.isPublished });
      toast.success(proj.isPublished ? 'Project unpublished' : 'Project published');
      fetchIProjects();
    } catch (err) { toast.error(err.message || 'Update failed'); }
  };

  const fetchMerchants = async () => {
    setMerchantLoading(true);
    try {
      const res = await merchantAPI.adminList(merchantFilter ? { status: merchantFilter } : {});
      setMerchants(res.merchants || []);
    } catch (err) { toast.error(err.message || 'Failed to load merchants'); }
    finally { setMerchantLoading(false); }
  };

  const handleRegisterMerchant = async () => {
    try {
      setRegLoading(true);
      await merchantAPI.adminCreate(regForm);
      toast.success('Merchant registered');
      setRegisterModal(false);
      setRegForm({ userId: '', businessName: '', businessType: 'retail', phone: '', email: '', settlementFrequency: 'on_demand', paymentFeePercent: 1.5 });
      setRegSelectedUser(null); setRegUserQuery(''); setRegUserResults([]);
      fetchMerchants();
    } catch (err) { toast.error(err.message || 'Failed to register merchant'); }
    finally { setRegLoading(false); }
  };

  const handleRegUserSearch = async (q) => {
    setRegUserQuery(q);
    setRegSelectedUser(null);
    setRegForm(p => ({ ...p, userId: '' }));
    if (q.trim().length < 2) { setRegUserResults([]); return; }
    setRegUserSearching(true);
    try {
      const r = await adminAPI.getUsers({ search: q.trim(), limit: 6, page: 1 });
      setRegUserResults(r.users || []);
    } catch { setRegUserResults([]); }
    finally { setRegUserSearching(false); }
  };

  const selectRegUser = (u) => {
    setRegSelectedUser(u);
    setRegForm(p => ({ ...p, userId: u._id, email: p.email || u.email || '', phone: p.phone || u.phone || '' }));
    setRegUserQuery(u.name + ' — ' + u.email);
    setRegUserResults([]);
  };

  const handleMerchantStatus = async (id, status) => {
    try {
      await merchantAPI.adminUpdate(id, { status });
      toast.success(`Merchant ${status}`);
      fetchMerchants();
      if (merchantDetail?._id === id) setMerchantDetail(prev => prev ? { ...prev, status } : prev);
    } catch (err) { toast.error(err.message || 'Update failed'); }
  };

  const handleMerchantSettle = async (id) => {
    if (!window.confirm('Force settle this merchant now?')) return;
    try {
      await merchantAPI.adminSettle(id);
      toast.success('Settlement processed');
      fetchMerchants();
    } catch (err) { toast.error(err.message || 'Settlement failed'); }
  };

  const openMerchantDetail = async (m) => {
    setMerchantDetail(m);
    try {
      const res = await merchantAPI.adminPayments(m._id, { limit: 10 });
      setMerchantPayments(res.payments || []);
    } catch { setMerchantPayments([]); }
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (tab === 'exchange')        fetchExchangeQueue(); },       [tab, fetchExchangeQueue]);
  useEffect(() => { if (tab === 'crypto-withdraw') fetchCryptoWithdrawals(); },   [tab, fetchCryptoWithdrawals]);
  useEffect(() => { if (tab === 'pricing')         fetchPricing(); },             [tab, fetchPricing]);
  useEffect(() => { if (tab === 'gateway')         fetchGateway(); },             [tab, fetchGateway]);
  useEffect(() => { if (tab === 'isp-payments')    fetchIspPayments(); },         [tab, fetchIspPayments]);
  useEffect(() => { if (tab === 'withdrawals')     fetchWithdrawals(); },         [tab, fetchWithdrawals]);
  useEffect(() => { if (tab === 'stuck-withdrawals') fetchStuckWithdrawals(); },   [tab, fetchStuckWithdrawals]);
  useEffect(() => { if (tab === 'merchants')       fetchMerchants(); },           [tab]);
  useEffect(() => { if (tab === 'projects')        fetchIProjects(); },            [tab]);
  useEffect(() => { if (tab === 'features')        fetchAdminFeatures(); },         [tab]);
  useEffect(() => { if (tab === 'fees')            fetchFeeRules(); },              [tab, fetchFeeRules]);
  useEffect(() => { if (tab === 'crypto-wallets')  fetchCryptoNets(); },            [tab]);
  useEffect(() => { if (tab === 'crypto-deposits')   fetchCryptoDeposits(); },      [tab]);
  useEffect(() => { if (tab === 'exchange-settings') { fetchExSettings(); fetchDeskState(); } }, [tab]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <ShieldX className="w-16 h-16 text-red mb-4" />
        <h2 className="text-text-primary font-bold text-xl mb-2">Access Denied</h2>
        <p className="text-text-muted text-sm">This area requires administrator privileges.</p>
      </div>
    );
  }

  const handleApproveKYC = async (userId, userName) => {
    try {
      await adminAPI.approveKYC(userId);
      toast.success(`KYC approved for ${userName}`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to approve KYC');
    }
  };

  const handleRejectKYC = async () => {
    if (!rejectModal) return;
    const finalReason = rejectReason === 'Other (specify below)' ? rejectReasonOther.trim() : rejectReason;
    if (!finalReason) { toast.error('Please select or enter a rejection reason'); return; }
    try {
      await adminAPI.rejectKYC(rejectModal._id, { reason: finalReason });
      toast.success('KYC rejected');
      setRejectModal(null);
      setRejectReason('');
      setRejectReasonOther('');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to reject KYC');
    }
  };

  const handleSuspend = async () => {
    if (!suspendModal) return;
    try {
      await adminAPI.suspendUser(suspendModal._id, { reason: suspendReason });
      toast.success(`${suspendModal.name} suspended`);
      setSuspendModal(null);
      setSuspendReason('');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to suspend user');
    }
  };

  const handleUnsuspend = async (userId, name) => {
    try {
      await adminAPI.unsuspendUser(userId);
      toast.success(`${name} unsuspended`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to unsuspend');
    }
  };

  // Help-desk tool: admin sets a temporary password for a locked-out
  // customer. Not sent automatically by this action — relay it to them
  // directly (call/SMS), same as any standard help-desk reset.
  const handleResetPassword = async () => {
    if (!resetPwModal) return;
    if (!resetPwValue || resetPwValue.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    try {
      await adminAPI.resetPassword(resetPwModal._id, { newPassword: resetPwValue });
      toast.success(`Password reset for ${resetPwModal.name}. Relay it to them directly — it was not sent automatically.`);
      setResetPwModal(null);
      setResetPwValue('');
    } catch (err) {
      toast.error(err.message || 'Failed to reset password');
    }
  };

  const handleApproveWithdrawal = async (txId) => {
    try {
      await adminAPI.approveWithdrawal(txId);
      toast.success('Withdrawal approved');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to approve withdrawal');
    }
  };

  const handleCryptoApprove = async (id) => {
    try {
      await cryptoWithdrawAPI.approve(id);
      toast.success('Withdrawal approved');
      fetchCryptoWithdrawals();
    } catch (err) {
      toast.error(err.message || 'Failed to approve');
    }
  };

  const handleCryptoMarkSent = async () => {
    if (!cryptoMarkSentModal) return;
    try {
      await cryptoWithdrawAPI.markSent(cryptoMarkSentModal._id, { txHash: cryptoMarkSentTxHash });
      toast.success('Marked as sent. Customer notified.');
      setCryptoMarkSentModal(null);
      setCryptoMarkSentTxHash('');
      fetchCryptoWithdrawals();
    } catch (err) {
      toast.error(err.message || 'Failed to mark sent');
    }
  };

  const handleCryptoComplete = async (id) => {
    try {
      await cryptoWithdrawAPI.complete(id);
      toast.success('Withdrawal completed!');
      fetchCryptoWithdrawals();
    } catch (err) {
      toast.error(err.message || 'Failed to complete');
    }
  };

  const handleCryptoReject = async () => {
    if (!cryptoRejectModal) return;
    try {
      await cryptoWithdrawAPI.reject(cryptoRejectModal._id, { reason: cryptoRejectReason });
      toast.success('Rejected. USDT refunded to customer.');
      setCryptoRejectModal(null);
      setCryptoRejectReason('');
      fetchCryptoWithdrawals();
    } catch (err) {
      toast.error(err.message || 'Failed to reject');
    }
  };

  const handleCryptoReconcile = async () => {
    setReconcileLoading(true);
    try {
      const res = await cryptoWithdrawAPI.reconcile();
      setCryptoReconcile(res.report);
    } catch (err) {
      toast.error(err.message || 'Reconciliation failed');
    } finally {
      setReconcileLoading(false);
    }
  };

  const handleMockModeToggle = async () => {
    setMockModeToggling(true);
    try {
      const current = mockModeConfig?.value === true || mockModeConfig?.value === 'true';
      await configAPI.set('CRYPTO_MOCK_MODE', !current);
      const updated = await configAPI.get('CRYPTO_MOCK_MODE');
      setMockModeConfig(updated.config || { value: !current });
      toast.success(`Mock mode ${!current ? 'ENABLED' : 'DISABLED'}`);
    } catch (err) {
      toast.error(err.message || 'Failed to toggle mock mode');
    } finally {
      setMockModeToggling(false);
    }
  };

  const fetchMockModeConfig = async () => {
    try {
      const res = await configAPI.get('CRYPTO_MOCK_MODE');
      setMockModeConfig(res.config || res);
    } catch { /* silent */ }
  };

  const handleApproveExchange = async (orderId) => {
    try {
      await exchangeAPI.approve(orderId);
      toast.success('Exchange order approved');
      fetchExchangeQueue();
    } catch (err) {
      toast.error(err.message || 'Failed to approve');
    }
  };

  const handleMarkSent = async () => {
    if (!markSentModal) return;
    try {
      await exchangeAPI.markSent(markSentModal._id, { txHash: markSentTxHash });
      toast.success('Order marked as sent. Customer notified.');
      setMarkSentModal(null);
      setMarkSentTxHash('');
      fetchExchangeQueue();
    } catch (err) {
      toast.error(err.message || 'Failed to mark sent');
    }
  };

  const handleRejectExchange = async () => {
    if (!rejectExchangeModal) return;
    try {
      await exchangeAPI.reject(rejectExchangeModal._id, { reason: rejectExchangeReason });
      toast.success('Order rejected. UGX refunded to customer.');
      setRejectExchangeModal(null);
      setRejectExchangeReason('');
      fetchExchangeQueue();
    } catch (err) {
      toast.error(err.message || 'Failed to reject');
    }
  };

  const copyAddr = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied!', { duration: 1500 }));
  };

  const handleProcessReturns = async () => {
    try {
      const res = await investmentAPI.processReturns({});
      toast.success(res.message || `Processed returns for ${res.count} investments`);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to process returns');
    }
  };

  const s = stats || {};

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">Admin Panel</h1>
          <p className="text-text-secondary text-sm mt-1">Platform management, users, KYC, transactions, and investments.</p>
        </div>
        <button onClick={fetchData} className="text-text-muted hover:text-cyan transition-colors p-2 rounded-xl hover:bg-cyan/10">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total Users"        value={s.users?.total || 0}                                   sub={`${s.users?.active || 0} active`}         color="#06C8E8" icon="👤"/>
          <StatCard label="Pending KYC"        value={s.users?.pendingKYC || 0}                               sub="Awaiting review"                           color="#EAB308" icon="🔖"/>
          <StatCard label="Total Deposits"     value={`$${(s.revenue?.totalDeposits || 0).toFixed(0)}`}       sub="Platform total"                            color="#10B981" icon="💰"/>
          <StatCard label="ISP Subscribers"    value={s.isp?.activeSubscribers || 0}                          sub={`${s.isp?.onlineRouters || 0} routers online`} color="#14B8A6" icon="📡"/>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          ['dashboard', 'Dashboard'],
          ['gateway', '🌐 Gateway'],
          ['pricing', '📈 Pricing'],
          ['exchange', `₮ Exchange (${exchangeQueue.filter(o => o.status === 'pending' || o.status === 'approved').length})`],
          ['crypto-withdraw', `⬆ Withdraw (${cryptoWithdrawals.filter(w => w.status === 'pending' || w.status === 'approved').length})`],
          ['users', 'Users'],
          ['kyc', `KYC (${kycQueue.length})`],
          ['transactions', 'Transactions'],
          ['investments', 'Investments'],
          ['isp-payments', `📡 ISP Pay (${ispPayments.filter(p => p.callbackStatus === 'failed').length || ''})`],
          ['withdrawals', `💸 Withdrawals (${withdrawals.filter(w => w.status === 'pending_approval').length || ''})`],
          ['stuck-withdrawals', `⏱ Stuck Withdrawals${stuckWithdrawals.length ? ` (${stuckWithdrawals.length})` : ''}`],
          ['merchants', `🏪 Merchants (${merchants.length})`],
          ['fees', '💰 Fee Rules'],
          ['crypto-wallets', '🔑 Crypto Wallets'],
          ['exchange-settings', '⚙ Exchange Settings'],
          ['crypto-deposits', `📥 Deposits (${cryptoDeposits.filter(d => d.status === 'awaiting_verification').length || ''})`],
          ['launch-event', '🎉 Launch Event'],
          ['partnership-network', '🤝 Partnership Network'],
          ['network', '📶 Network'],
          ['market', newOrders ? `🛒 DwiSend — ${newOrders} NEW order${newOrders > 1 ? 's' : ''}` : '🛒 DwiSend'],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={clsx(
              'px-4 py-2 rounded-xl text-sm font-semibold transition-all',
              tab === id ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi',
              id === 'market' && newOrders > 0 && 'ring-2 ring-red animate-pulse'
            )}
          >{label}</button>
        ))}
        <a href="/treasury-admin"
          className="px-4 py-2 rounded-xl text-sm font-semibold bg-bg-panel text-text-secondary border border-border hover:border-border-hi transition-all whitespace-nowrap">
          🏦 Treasury
        </a>
        <a href="/countries-admin"
          className="px-4 py-2 rounded-xl text-sm font-semibold bg-bg-panel text-text-secondary border border-border hover:border-border-hi transition-all whitespace-nowrap">
          🌍 Countries
        </a>
        <a href="/cms-admin"
          className="px-4 py-2 rounded-xl text-sm font-semibold bg-bg-panel text-text-secondary border border-border hover:border-border-hi transition-all whitespace-nowrap">
          CMS
        </a>
                  <a href="/operations"
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-bg-panel text-text-secondary border border-border hover:border-border-hi transition-all whitespace-nowrap">
                    Operations
                  </a>
                  <a href="/admin-kyc"
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-bg-panel text-text-secondary border border-border hover:border-border-hi transition-all whitespace-nowrap">
                    KYC Queue
                  </a>
                  <a href="/superadmin"
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-bg-panel text-text-secondary border border-border hover:border-border-hi transition-all whitespace-nowrap">
                    Super Admin
                  </a>
        <a href="/executive"
          className="px-4 py-2 rounded-xl text-sm font-semibold bg-cyan text-bg border border-cyan hover:opacity-90 transition-all whitespace-nowrap font-bold">
          📊 Executive
        </a>
      </div>

      {/* DASHBOARD */}
      {tab === 'dashboard' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card>
            <SectionHeader title="Quick Actions" />
            <div className="space-y-2">
              <Button className="w-full" onClick={handleProcessReturns}>
                <Zap className="w-4 h-4" /> Process Monthly Investment Returns
              </Button>
              <p className="text-text-muted text-xs text-center">
                Calculates and credits returns for all active investment positions.
              </p>
            </div>
          </Card>

          <Card>
            <SectionHeader title="KYC Queue" />
            {kycQueue.length === 0 ? (
              <p className="text-text-muted text-sm text-center py-4">No pending KYC submissions</p>
            ) : (
              <div className="space-y-2">
                {kycQueue.slice(0, 4).map((u) => (
                  <div key={u._id} className="flex items-center justify-between p-3 rounded-xl bg-bg-panel border border-border">
                    <div>
                      <p className="text-text-primary text-xs font-semibold">{u.name}</p>
                      <p className="text-text-muted text-[10px]">{u.kyc.documentType?.replace('_', ' ')}</p>
                      {u.kyc.submissionAttempts >= 2 && (
                        <span className="inline-block text-[9px] font-bold bg-amber/15 text-amber border border-amber/30 rounded px-1.5 py-0.5 mt-0.5">
                          Resubmission #{u.kyc.submissionAttempts}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <Button variant="success" size="xs" onClick={() => handleApproveKYC(u._id, u.name)}>
                        ✓
                      </Button>
                      <Button variant="danger" size="xs" onClick={() => setRejectModal(u)}>
                        ✗
                      </Button>
                    </div>
                  </div>
                ))}
                {kycQueue.length > 4 && (
                  <button onClick={() => setTab('kyc')} className="text-cyan text-xs hover:underline w-full text-center">
                    View all {kycQueue.length} pending →
                  </button>
                )}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* USERS */}
      {tab === 'users' && (
        <Card>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="text-text-primary font-bold text-sm">User Management ({userTotal})</h3>
            <div className="flex gap-2 items-center">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search name, email, phone..."
                  className="bg-bg-panel border border-border rounded-xl pl-8 pr-3 py-1.5 text-text-primary text-xs outline-none focus:border-cyan w-56"
                  value={userSearch}
                  onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : users.length === 0 ? (
            <Empty icon="👤" title="No users found" />
          ) : (
            <>
              <Table headers={['Name', 'Email', 'Phone', 'Role', 'KYC', 'Balance', 'Status', 'Actions']}>
                {users.map((u) => (
                  <tr key={u._id}>
                    <Td className="font-semibold text-text-primary">
                      <div className="flex items-center gap-1.5">
                        <span>{u.name}</span>
                        <button
                          title={`Copy ID: ${u._id}`}
                          onClick={() => { navigator.clipboard.writeText(u._id); toast.success('ID copied'); }}
                          className="text-text-muted hover:text-cyan transition-colors flex-shrink-0"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </Td>
                    <Td className="text-xs">{u.email}</Td>
                    <Td className="text-xs">{u.phone}</Td>
                    <Td><Badge label={u.role?.replace('_', ' ') || 'client'} color="cyan" /></Td>
                    <Td><Badge label={u.kyc?.status || 'none'} color={u.kyc?.status === 'approved' ? 'green' : u.kyc?.status === 'pending' ? 'amber' : 'gray'} /></Td>
                    <Td className="text-cyan font-bold text-xs">${(u.wallet?.balance || 0).toFixed(2)}</Td>
                    <Td><Badge label={u.isSuspended ? 'Suspended' : 'Active'} color={u.isSuspended ? 'red' : 'green'} /></Td>
                    <Td>
                      <div className="flex gap-1">
                        {u.isSuspended ? (
                          <Button variant="ghost" size="xs" onClick={() => handleUnsuspend(u._id, u.name)}>Unsuspend</Button>
                        ) : (
                          <Button variant="ghost" size="xs" onClick={() => setSuspendModal(u)}>Suspend</Button>
                        )}
                        <Button variant="ghost" size="xs" onClick={() => setResetPwModal(u)}>Reset PW</Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
              {userTotal > 20 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-text-muted text-xs">{userTotal} users</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" disabled={userPage === 1} onClick={() => setUserPage(p => p - 1)}>Prev</Button>
                    <span className="text-text-muted text-xs self-center">Page {userPage}</span>
                    <Button variant="ghost" size="sm" disabled={userPage * 20 >= userTotal} onClick={() => setUserPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* ── PRICING ENGINE ── */}
      {tab === 'pricing' && (
        <div className="space-y-6">
          {priceLoading ? (
            <div className="flex justify-center py-16"><Spinner size="lg" /></div>
          ) : (
            <>
              {/* Live rate cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Market Price" value={priceData ? `UGX ${(priceData.marketUgxPerUsdt || 0).toLocaleString()}` : '—'} sub="CoinGecko · per 1 USDT" color="cyan" />
                <StatCard label="DwiPay Buy Rate" value={priceData ? `UGX ${(priceData.buyUgxPerUsdt || 0).toLocaleString()}` : '—'} sub={`Spread: ${priceData ? (priceData.buySpread * 100).toFixed(1) + '%' : '—'}`} color="green" />
                <StatCard label="Margin per USDT" value={priceData ? `UGX ${(priceData.marginPerUsdt || 0).toLocaleString()}` : '—'} sub="Buy spread income" color="amber" />
                <StatCard label="Price Source" value={priceData?.priceSource === 'coingecko' ? 'CoinGecko' : 'Manual'} sub={priceData ? `Age: ${priceData.priceAgeSeconds}s` : '—'} color="gray" />
              </div>

              {/* Price refresh + status */}
              <Card>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <SectionHeader title="Price Cache" />
                    {priceData && (
                      <p className="text-text-muted text-sm mt-1">
                        Last fetched: <span className="text-text-primary font-semibold">{priceData.fetchedAt ? new Date(priceData.fetchedAt).toLocaleString() : '—'}</span>
                        {' · '}Source: <span className="text-cyan font-semibold">{priceData.priceSource || '—'}</span>
                        {' · '}Age: <span className={priceData.priceAgeSeconds > 120 ? 'text-amber font-semibold' : 'text-green font-semibold'}>{priceData.priceAgeSeconds}s</span>
                      </p>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button variant="ghost" size="sm" onClick={() => fetchPricing()}>
                      <RefreshCw className="w-4 h-4" /> Refresh Data
                    </Button>
                    <Button size="sm" loading={priceRefreshing} onClick={handlePriceRefresh}>
                      <Zap className="w-4 h-4" /> Clear Price Cache
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Config editor */}
              <Card>
                <SectionHeader title="Pricing Configuration" />
                <p className="text-text-muted text-sm mb-5">Changes take effect within 5 minutes (config cache). No restart needed.</p>
                {priceConfigs.length === 0 ? (
                  <p className="text-text-muted text-sm">No pricing config keys found. Run the backend to seed defaults.</p>
                ) : (
                  <div className="space-y-3">
                    {priceConfigs.map(cfg => (
                      <div key={cfg.key} className="flex items-center gap-3 p-3 rounded-xl bg-bg-panel border border-border hover:border-border-hi transition-all">
                        <div className="flex-1 min-w-0">
                          <p className="text-text-primary text-sm font-semibold truncate">{cfg.label || cfg.key}</p>
                          <p className="text-text-muted text-xs font-mono">{cfg.key}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {cfg.type === 'boolean' ? (
                            <select
                              className="bg-bg border border-border rounded-lg px-3 py-1.5 text-text-primary text-sm outline-none focus:border-cyan"
                              value={priceEditing[cfg.key] ?? String(cfg.value)}
                              onChange={e => setPriceEditing(s => ({ ...s, [cfg.key]: e.target.value }))}
                            >
                              <option value="true">Enabled</option>
                              <option value="false">Disabled</option>
                            </select>
                          ) : (
                            <input
                              type="number"
                              step="any"
                              className="bg-bg border border-border rounded-lg px-3 py-1.5 text-text-primary text-sm outline-none focus:border-cyan w-28 text-right font-mono"
                              value={priceEditing[cfg.key] ?? cfg.value}
                              onChange={e => setPriceEditing(s => ({ ...s, [cfg.key]: e.target.value }))}
                            />
                          )}
                          <Button
                            size="sm"
                            loading={!!priceSaving[cfg.key]}
                            onClick={() => handlePriceConfigSave(cfg.key)}
                            disabled={String(priceEditing[cfg.key]) === String(cfg.value)}
                          >Save</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      )}

      {/* EXCHANGE QUEUE */}
      {tab === 'exchange' && (
        <Card>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="text-text-primary font-bold text-sm">
              Crypto Exchange Queue ({exchangeQueue.length})
            </h3>
            <div className="flex gap-2 items-center">
              <select
                className="bg-bg-panel border border-border rounded-xl px-3 py-1.5 text-text-primary text-xs outline-none focus:border-cyan"
                value={exchangeFilter}
                onChange={e => setExchangeFilter(e.target.value)}
              >
                <option value="">Active (pending + approved)</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="sent">Sent</option>
                <option value="rejected">Rejected</option>
              </select>
              <button onClick={fetchExchangeQueue} className="text-text-muted hover:text-cyan p-1.5 rounded-lg hover:bg-cyan/10 transition-all">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {exchangeLoading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : exchangeQueue.length === 0 ? (
            <Empty icon="₮" title="No exchange orders" description="Customer exchange orders will appear here." />
          ) : (
            <Table headers={['Customer', 'UGX Paid', 'USDT', 'Rate', 'Network', 'Destination Wallet', 'Status', 'Time', 'Actions']}>
              {exchangeQueue.map(o => {
                const statusColors = { pending: 'amber', approved: 'cyan', sent: 'green', rejected: 'red', cancelled: 'gray' };
                return (
                  <tr key={o._id}>
                    <Td>
                      <div>
                        <p className="font-semibold text-text-primary text-xs">{o.user?.name || '—'}</p>
                        <p className="text-text-muted text-[10px]">{o.user?.phone || o.user?.email}</p>
                      </div>
                    </Td>
                    <Td className="font-bold text-sm">{o.fromAmount?.toLocaleString()} UGX</Td>
                    <Td>
                      <p className="font-black text-green text-sm">{o.netAmount} USDT</p>
                      <p className="text-text-muted text-[10px]">gross: {o.grossAmount}</p>
                    </Td>
                    <Td className="text-xs text-text-secondary">{o.ugxPerUsdt?.toLocaleString()} / USDT</Td>
                    <Td>
                      <Badge label={o.network} color="purple" />
                    </Td>
                    <Td>
                      <button
                        onClick={() => copyAddr(o.destinationWallet)}
                        className="text-xs font-mono text-text-secondary hover:text-cyan flex items-center gap-1"
                      >
                        {o.destinationWallet?.length > 14
                          ? o.destinationWallet.slice(0, 6) + '…' + o.destinationWallet.slice(-4)
                          : o.destinationWallet}
                        <Copy className="w-3 h-3" />
                      </button>
                    </Td>
                    <Td>
                      <Badge label={o.status} color={statusColors[o.status] || 'gray'} />
                      {o.txHash && (
                        <button onClick={() => copyAddr(o.txHash)} className="text-[10px] text-cyan hover:underline flex items-center gap-1 mt-0.5">
                          TX: {o.txHash.slice(0, 8)}… <Copy className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </Td>
                    <Td className="text-xs text-text-muted whitespace-nowrap">
                      {o.createdAt ? format(new Date(o.createdAt), 'dd MMM HH:mm') : '—'}
                    </Td>
                    <Td>
                      <div className="flex gap-1 flex-wrap">
                        {o.status === 'pending' && (
                          <>
                            <Button variant="success" size="xs" onClick={() => handleApproveExchange(o._id)}>
                              <CheckCircle className="w-3 h-3" /> Approve
                            </Button>
                            <Button variant="danger" size="xs" onClick={() => setRejectExchangeModal(o)}>
                              Reject
                            </Button>
                          </>
                        )}
                        {o.status === 'approved' && (
                          <>
                            <Button size="xs" onClick={() => setMarkSentModal(o)}>
                              <Send className="w-3 h-3" /> Mark Sent
                            </Button>
                            <Button variant="danger" size="xs" onClick={() => setRejectExchangeModal(o)}>
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </Table>
          )}
        </Card>
      )}

      {/* CRYPTO WITHDRAWALS */}
      {tab === 'crypto-withdraw' && (
        <div className="space-y-4">

          {/* ── Mock Mode Control ── */}
          <div className={clsx(
            'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3',
            mockModeConfig?.value ? 'bg-amber/5 border-amber/30' : 'bg-bg-panel border-border'
          )}>
            <div className="flex items-center gap-3">
              <span className="text-lg">{mockModeConfig?.value ? '🧪' : '🔒'}</span>
              <div>
                <p className="text-text-primary text-sm font-bold">
                  Mock Blockchain Mode
                  {' '}
                  <Badge
                    label={mockModeConfig?.value ? 'ENABLED' : 'DISABLED'}
                    color={mockModeConfig?.value ? 'amber' : 'gray'}
                  />
                </p>
                <p className="text-text-muted text-[10px] mt-0.5">
                  {mockModeConfig?.value
                    ? 'ACTIVE — New withdrawals will auto-complete with fake TX IDs. No real funds are moved.'
                    : 'OFF — Withdrawals require manual admin approval and real blockchain TX hash.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={mockModeConfig?.value ? 'danger' : 'outline'}
                loading={mockModeToggling}
                onClick={handleMockModeToggle}
              >
                {mockModeConfig?.value ? '🔴 Disable Mock Mode' : '🧪 Enable Mock Mode'}
              </Button>
              <Button size="sm" variant="outline" loading={reconcileLoading} onClick={handleCryptoReconcile}>
                <RefreshCw className="w-3 h-3" /> Reconcile
              </Button>
            </div>
          </div>

          {/* ── Reconciliation Report ── */}
          {cryptoReconcile && (
            <Card>
              <div className="flex items-center justify-between mb-3">
                <p className="text-text-primary text-sm font-bold">USDT Reconciliation Report</p>
                <p className="text-text-muted text-[10px]">{new Date(cryptoReconcile.generatedAt).toLocaleString()}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div className="bg-bg-elevated border border-border rounded-xl p-3">
                  <p className="text-text-muted text-[10px] uppercase tracking-widest">Locked (Pending)</p>
                  <p className="text-amber font-black text-lg">₮ {(cryptoReconcile.locked?.usdt || 0).toFixed(2)}</p>
                  <p className="text-text-muted text-[10px]">{cryptoReconcile.locked?.count} records</p>
                </div>
                <div className="bg-bg-elevated border border-border rounded-xl p-3">
                  <p className="text-text-muted text-[10px] uppercase tracking-widest">Disbursed (Net)</p>
                  <p className="text-green font-black text-lg">₮ {(cryptoReconcile.disbursed?.usdt || 0).toFixed(2)}</p>
                  <p className="text-text-muted text-[10px]">{cryptoReconcile.disbursed?.count} sent/completed</p>
                </div>
                <div className="bg-bg-elevated border border-border rounded-xl p-3">
                  <p className="text-text-muted text-[10px] uppercase tracking-widest">Fees Earned</p>
                  <p className="text-cyan font-black text-lg">₮ {(cryptoReconcile.disbursed?.feesUsdt || 0).toFixed(2)}</p>
                  <p className="text-text-muted text-[10px]">from completed</p>
                </div>
                <div className="bg-bg-elevated border border-border rounded-xl p-3">
                  <p className="text-text-muted text-[10px] uppercase tracking-widest">Mock Records</p>
                  <p className="text-amber font-black text-lg">{cryptoReconcile.mockRecords?.count || 0}</p>
                  <p className="text-text-muted text-[10px]">₮ {(cryptoReconcile.mockRecords?.usdt || 0).toFixed(2)} gross</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-text-muted border-b border-border">
                      <th className="text-left py-2 px-2 font-bold">Status</th>
                      <th className="text-right py-2 px-2 font-bold">Count</th>
                      <th className="text-right py-2 px-2 font-bold">Gross USDT</th>
                      <th className="text-right py-2 px-2 font-bold">Net USDT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(cryptoReconcile.byStatus || {}).map(([status, data]) => (
                      <tr key={status} className="border-b border-border/30 last:border-0">
                        <td className="py-2 px-2"><Badge label={status} color={status === 'completed' ? 'green' : status === 'pending' ? 'amber' : status === 'rejected' ? 'red' : 'gray'} /></td>
                        <td className="py-2 px-2 text-right text-text-primary font-semibold">{data.count}</td>
                        <td className="py-2 px-2 text-right text-text-primary">₮ {(data.grossUsdt || 0).toFixed(4)}</td>
                        <td className="py-2 px-2 text-right text-green">₮ {(data.netUsdt || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-text-muted text-[10px] mt-2">{cryptoReconcile.note}</p>
            </Card>
          )}

          {/* Treasury stats */}
          {cryptoTreasury && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard label="Pending (₮)"    value={`₮ ${(cryptoTreasury.pendingUsdt || 0).toFixed(2)}`}  sub={`${cryptoTreasury.pendingCount || 0} requests`}    color="#F59E0B" icon="⏳"/>
              <StatCard label="Today Volume"   value={`₮ ${(cryptoTreasury.dailyVolume || 0).toFixed(2)}`} sub={`${cryptoTreasury.dailyCount || 0} withdrawals today`} color="#06C8E8" icon="📊"/>
              <StatCard label="Platform Fees"  value={`₮ ${(cryptoTreasury.dailyFees || 0).toFixed(2)}`}   sub="Fees earned today"                                  color="#10B981" icon="💰"/>
              <StatCard label="Sent / Done"    value={cryptoTreasury.sentCount + cryptoTreasury.completedCount} sub={`${cryptoTreasury.completedCount} completed`}   color="#8B5CF6" icon="🚀"/>
            </div>
          )}

          <Card>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="text-text-primary font-bold text-sm">
                Crypto Withdrawal Queue ({cryptoWithdrawals.length})
              </h3>
              <div className="flex gap-2 items-center">
                <select
                  className="bg-bg-panel border border-border rounded-xl px-3 py-1.5 text-text-primary text-xs outline-none focus:border-cyan"
                  value={cryptoFilter}
                  onChange={e => setCryptoFilter(e.target.value)}
                >
                  <option value="">Active (pending + approved)</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="sent">Sent</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                </select>
                <button onClick={fetchCryptoWithdrawals} className="text-text-muted hover:text-cyan p-1.5 rounded-lg hover:bg-cyan/10 transition-all">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {cryptoLoading ? (
              <div className="flex justify-center py-12"><Spinner size="lg" /></div>
            ) : cryptoWithdrawals.length === 0 ? (
              <Empty icon="₮" title="No withdrawals" description="Customer USDT withdrawal requests will appear here." />
            ) : (
              <Table headers={['Customer', 'Amount', 'Net USDT', 'Network', 'Destination Wallet', 'Status', 'TX Hash', 'Time', 'Actions']}>
                {cryptoWithdrawals.map(w => {
                  const statusColors = { pending: 'amber', approved: 'cyan', sent: 'purple', completed: 'green', rejected: 'red', cancelled: 'gray' };
                  const shortAddr = (addr) => addr?.length > 14 ? addr.slice(0, 6) + '…' + addr.slice(-4) : addr;
                  return (
                    <tr key={w._id}>
                      <td className="px-3 py-2 text-xs whitespace-nowrap">
                        <div>
                          <p className="font-semibold text-text-primary">{w.user?.name || '—'}</p>
                          <p className="text-text-muted">{w.user?.phone || w.user?.email}</p>
                        </div>
                      </td>
                      <Td className="font-bold text-sm">₮ {w.amount}</Td>
                      <Td>
                        <p className="font-black text-green text-sm">₮ {w.netAmount}</p>
                        <p className="text-text-muted text-[10px]">fees: {(w.networkFee + w.platformFee).toFixed(2)}</p>
                      </Td>
                      <Td><Badge label={w.network} color="purple" /></Td>
                      <Td>
                        <button
                          onClick={() => copyAddr(w.destinationWallet)}
                          className="text-xs font-mono text-text-secondary hover:text-cyan flex items-center gap-1"
                        >
                          {shortAddr(w.destinationWallet)}
                          <Copy className="w-3 h-3" />
                        </button>
                      </Td>
                      <Td>
                        <Badge label={w.status} color={statusColors[w.status] || 'gray'} />
                      </Td>
                      <Td>
                        {w.isMock && <Badge label="MOCK" color="amber" />}
                        {w.txHash ? (
                          <div className="flex flex-col gap-0.5 mt-1">
                            <button onClick={() => copyAddr(w.txHash)} className="text-[10px] text-cyan hover:underline flex items-center gap-1 font-mono">
                              {w.txHash.slice(0, 8)}… <Copy className="w-2.5 h-2.5" />
                            </button>
                            {w.explorerUrl && (
                              <a href={w.explorerUrl} target="_blank" rel="noreferrer" className="text-[10px] text-purple hover:underline">
                                View on chain ↗
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-text-muted text-xs">—</span>
                        )}
                      </Td>
                      <Td className="text-xs text-text-muted whitespace-nowrap">
                        {w.createdAt ? format(new Date(w.createdAt), 'dd MMM HH:mm') : '—'}
                      </Td>
                      <Td>
                        <div className="flex gap-1 flex-wrap">
                          {w.status === 'pending' && (
                            <>
                              <Button variant="success" size="xs" onClick={() => handleCryptoApprove(w._id)}>
                                <CheckCircle className="w-3 h-3" /> Approve
                              </Button>
                              <Button variant="danger" size="xs" onClick={() => setCryptoRejectModal(w)}>
                                Reject
                              </Button>
                            </>
                          )}
                          {w.status === 'approved' && (
                            <>
                              <Button size="xs" onClick={() => setCryptoMarkSentModal(w)}>
                                <Send className="w-3 h-3" /> Mark Sent
                              </Button>
                              <Button variant="danger" size="xs" onClick={() => setCryptoRejectModal(w)}>
                                Reject
                              </Button>
                            </>
                          )}
                          {w.status === 'sent' && (
                            <Button variant="success" size="xs" onClick={() => handleCryptoComplete(w._id)}>
                              ✓ Complete
                            </Button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </Card>
        </div>
      )}

      {/* KYC */}
      {tab === 'kyc' && (
        <Card>
          <SectionHeader title={`KYC Queue — ${kycQueue.length} Pending`} />
          {kycQueue.length === 0 ? (
            <Empty icon="✅" title="No pending KYC" description="All submissions have been reviewed." />
          ) : (
            <Table headers={['Name', 'Email', 'Document Type', 'Submitted', 'Actions']}>
              {kycQueue.map((u) => (
                <tr key={u._id}>
                  <Td className="font-semibold text-text-primary">{u.name}</Td>
                  <Td className="text-xs">{u.email}</Td>
                  <Td><Badge label={u.kyc?.documentType?.replace('_', ' ') || 'unknown'} color="blue" /></Td>
                  <Td className="text-xs text-text-muted">
                    {u.kyc?.submittedAt ? format(new Date(u.kyc.submittedAt), 'dd MMM yyyy') : '—'}
                  </Td>
                  <Td>
                    <div className="flex gap-2">
                      {u.kyc?.documentFront && (
                        <button
                          onClick={() => {
                            const token = localStorage.getItem('token') || localStorage.getItem('dwi_token') || '';
                            fetch('/api/kyc/admin/file?url=' + encodeURIComponent(u.kyc.documentFront), {
                              headers: { Authorization: 'Bearer ' + token },
                            })
                              .then(r => { if (!r.ok) throw new Error('Access denied'); return r.blob(); })
                              .then(b => window.open(URL.createObjectURL(b), '_blank'))
                              .catch(e => alert('Cannot load document: ' + e.message));
                          }}
                          className="text-cyan text-xs hover:underline cursor-pointer bg-transparent border-0 p-0"
                        >View Doc</button>
                      )}
                      <Button variant="success" size="xs" onClick={() => handleApproveKYC(u._id, u.name)}>
                        <ShieldCheck className="w-3 h-3" /> Approve
                      </Button>
                      <Button variant="danger" size="xs" onClick={() => setRejectModal(u)}>
                        <ShieldX className="w-3 h-3" /> Reject
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* TRANSACTIONS */}
      {tab === 'transactions' && (
        <Card>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="text-text-primary font-bold text-sm">Transactions ({txTotal})</h3>
            <div className="flex gap-2">
              <select
                className="bg-bg-panel border border-border rounded-xl px-3 py-1.5 text-text-primary text-xs outline-none focus:border-cyan"
                value={txFilter}
                onChange={(e) => { setTxFilter(e.target.value); setTxPage(1); }}
              >
                <option value="">All Types</option>
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
                <option value="investment_in">Investment</option>
                <option value="commission">Commission</option>
              </select>
              <select
                className="bg-bg-panel border border-border rounded-xl px-3 py-1.5 text-text-primary text-xs outline-none focus:border-cyan"
                value={txStatus}
                onChange={(e) => { setTxStatus(e.target.value); setTxPage(1); }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          ) : transactions.length === 0 ? (
            <Empty icon="📄" title="No transactions" />
          ) : (
            <>
              <Table headers={['User', 'Type', 'Amount', 'Method', 'Status', 'Date', 'Actions']}>
                {transactions.map((tx) => (
                  <tr key={tx._id}>
                    <Td className="text-xs">
                      <div>
                        <p className="font-semibold text-text-primary">{tx.user?.name || '—'}</p>
                        <p className="text-text-muted">{tx.user?.phone || tx.user?.email}</p>
                      </div>
                    </Td>
                    <Td><Badge label={tx.type.replace(/_/g, ' ')} color="cyan" /></Td>
                    <Td className="font-bold text-cyan">${tx.amount?.toFixed(2)}</Td>
                    <Td className="text-xs">{tx.paymentMethod?.replace(/_/g, ' ') || 'internal'}</Td>
                    <Td>
                      <Badge
                        label={tx.status}
                        color={tx.status === 'completed' ? 'green' : tx.status === 'processing' || tx.status === 'pending' ? 'amber' : 'red'}
                      />
                    </Td>
                    <Td className="text-xs text-text-muted">
                      {tx.createdAt ? format(new Date(tx.createdAt), 'dd MMM yy HH:mm') : '—'}
                    </Td>
                    <Td>
                      {(tx.type === 'withdrawal' && (tx.status === 'pending' || tx.status === 'processing')) && (
                        <Button variant="success" size="xs" onClick={() => handleApproveWithdrawal(tx._id)}>
                          Approve
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
              {txTotal > 20 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-text-muted text-xs">{txTotal} transactions</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" disabled={txPage === 1} onClick={() => setTxPage(p => p - 1)}>Prev</Button>
                    <span className="text-text-muted text-xs self-center">Page {txPage}</span>
                    <Button variant="ghost" size="sm" disabled={txPage * 20 >= txTotal} onClick={() => setTxPage(p => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* INVESTMENTS */}
      {/* PAYMENT GATEWAY REGISTRY */}
      {tab === 'gateway' && (
        <div className="space-y-4">
          {/* Header controls */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={() => setGwAddModal(true)}>+ Register Provider</Button>
            <Button variant="secondary" onClick={fetchGwHealth} disabled={gwHealthLoading}>
              <RefreshCw className={`w-4 h-4 ${gwHealthLoading ? 'animate-spin' : ''}`} /> Health Check All
            </Button>
          </div>

          {/* Provider table */}
          <Card>
            <SectionHeader title="Registered Providers" />
            {gwLoading ? <Spinner /> : gwProviders.length === 0 ? (
              <Empty icon="🌐" title="No providers registered" description="Register a payment provider to get started." />
            ) : (
              <Table headers={['Provider', 'Country', 'Currency', 'Operations', 'Priority', 'Mode', 'Status', '']}>
                {gwProviders.map((p) => {
                  const h = gwHealth.find(r => r.provider === p.name);
                  return (
                    <tr key={p._id}>
                      <Td>
                        <div className="font-semibold text-text-primary">{p.displayName}</div>
                        <div className="text-text-muted text-xs font-mono">{p.name}</div>
                        {p.description && <div className="text-text-muted text-xs mt-0.5">{p.description}</div>}
                      </Td>
                      <Td><Badge label={p.country} color="blue" /></Td>
                      <Td><Badge label={p.currency} color="purple" /></Td>
                      <Td className="text-xs space-y-1">
                        {p.depositEnabled && <div><Badge label="Deposit" color="green" /></div>}
                        {p.payoutEnabled  && <div><Badge label="Payout"  color="cyan"  /></div>}
                      </Td>
                      <Td className="font-mono text-sm">{p.priority}</Td>
                      <Td>
                        <Badge label={p.credentialMode.toUpperCase()} color={p.credentialMode === 'env' ? 'amber' : 'purple'} />
                      </Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          <Badge label={p.active ? 'Active' : 'Inactive'} color={p.active ? 'green' : 'red'} />
                          {h && (
                            <span title={h.message || ''}>
                              <Badge label={h.healthy ? '✓ Healthy' : '✗ Down'} color={h.healthy ? 'green' : 'red'} />
                            </span>
                          )}
                        </div>
                        {h?.latency != null && <div className="text-text-muted text-xs mt-0.5">{h.latency}ms</div>}
                      </Td>
                      <Td>
                        <Button
                          variant={p.active ? 'secondary' : 'primary'}
                          className="text-xs py-1 px-2"
                          onClick={() => handleGwToggle(p)}
                        >
                          {p.active ? 'Deactivate' : 'Activate'}
                        </Button>
                      </Td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </Card>

          {/* Installed adapters */}
          {gwAdapters.length > 0 && (
            <Card>
              <SectionHeader title="Installed Adapters" />
              <div className="grid sm:grid-cols-3 gap-3">
                {gwAdapters.map(a => (
                  <div key={a.name} className="bg-bg-panel border border-border rounded-xl p-3">
                    <div className="font-semibold text-text-primary text-sm">{a.displayName}</div>
                    <div className="text-text-muted text-xs font-mono mb-2">{a.name}</div>
                    {a.credentialKeys.length > 0 && (
                      <div className="space-y-0.5">
                        <div className="text-text-muted text-xs font-semibold">Required env vars:</div>
                        {a.credentialKeys.map(k => (
                          <div key={k} className="text-xs font-mono text-amber">{k}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-text-muted text-xs mt-3">
                To add a new adapter, create <code className="bg-bg-panel px-1 rounded">backend/services/gateway/providers/yourProvider.js</code> and register it in <code className="bg-bg-panel px-1 rounded">gatewayService.js</code>.
              </p>
            </Card>
          )}

          {/* How-to */}
          <Card>
            <SectionHeader title="Adding a New Country" />
            <div className="text-text-secondary text-sm space-y-2">
              <p>To accept payments in a new country, click <strong>Register Provider</strong> and fill in:</p>
              <ul className="list-disc list-inside space-y-1 text-text-muted text-xs">
                <li><strong>Name</strong> — unique slug, e.g. <code>mpesa_ke</code></li>
                <li><strong>Country</strong> — ISO code, e.g. <code>KE</code></li>
                <li><strong>Currency</strong> — e.g. <code>KES</code></li>
                <li><strong>Priority</strong> — lower = preferred when multiple providers match</li>
                <li><strong>Credential keys</strong> — comma-separated env var names needed</li>
              </ul>
              <p className="text-text-muted text-xs">The Wallet, Exchange, Dashboard, and Transactions do not need any changes — they route through the gateway automatically.</p>
            </div>
          </Card>
        </div>
      )}

      {/* PAYMENT GATEWAY — ADD PROVIDER MODAL */}
      <Modal open={gwAddModal} onClose={() => setGwAddModal(false)} title="Register Payment Provider" size="md">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Provider slug (unique)" placeholder="mpesa_ke" value={gwForm.name}        onChange={e => setGwForm(f => ({ ...f, name:        e.target.value }))} />
            <Input label="Display name"           placeholder="M-Pesa Kenya" value={gwForm.displayName} onChange={e => setGwForm(f => ({ ...f, displayName: e.target.value }))} />
            <Input label="Country (ISO)"          placeholder="KE"           value={gwForm.country}     onChange={e => setGwForm(f => ({ ...f, country:     e.target.value.toUpperCase() }))} />
            <Input label="Currency"               placeholder="KES"          value={gwForm.currency}    onChange={e => setGwForm(f => ({ ...f, currency:    e.target.value.toUpperCase() }))} />
            <Input label="Priority (1=highest)"  type="number" value={gwForm.priority}    onChange={e => setGwForm(f => ({ ...f, priority:    e.target.value }))} />
            <Input label="Webhook URL (optional)" placeholder="/api/gateway/webhook/mpesa_ke" value={gwForm.webhookUrl} onChange={e => setGwForm(f => ({ ...f, webhookUrl:  e.target.value }))} />
          </div>
          <Input label="Credential env var names (comma-separated)" placeholder="MPESA_CONSUMER_KEY, MPESA_CONSUMER_SECRET" value={gwForm.credentialKeys} onChange={e => setGwForm(f => ({ ...f, credentialKeys: e.target.value }))} />
          <Input label="Description (optional)" placeholder="M-Pesa Daraja API for Kenya" value={gwForm.description} onChange={e => setGwForm(f => ({ ...f, description: e.target.value }))} />
          <div className="flex gap-4 text-sm text-text-secondary">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={gwForm.depositEnabled} onChange={e => setGwForm(f => ({ ...f, depositEnabled: e.target.checked }))} /> Deposit enabled
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={gwForm.payoutEnabled} onChange={e => setGwForm(f => ({ ...f, payoutEnabled: e.target.checked }))} /> Payout enabled
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={gwForm.active} onChange={e => setGwForm(f => ({ ...f, active: e.target.checked }))} /> Active
            </label>
          </div>
          <div className="bg-amber/10 border border-amber/30 rounded-xl p-3 text-xs text-amber">
            After registering, create the adapter file <code>backend/services/gateway/providers/{gwForm.name || 'your_provider'}.js</code> and add it to <code>ADAPTERS</code> in <code>gatewayService.js</code>.
          </div>
          <Button className="w-full" onClick={handleGwAdd}>Register Provider</Button>
        </div>
      </Modal>

      {tab === 'investments' && (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <SectionHeader title="Investment Funds" />
              <Button onClick={handleProcessReturns}>
                <Zap className="w-4 h-4" /> Process Returns
              </Button>
            </div>
            {funds.length === 0 ? (
              <Empty icon="📊" title="No investment funds" />
            ) : (
              <Table headers={['Fund', 'Total Raised', 'Investors', 'Returns Paid', 'Rate']}>
                {funds.map((f) => (
                  <tr key={f._id}>
                    <Td className="font-semibold text-text-primary">{f.name}</Td>
                    <Td className="text-cyan font-bold">${(f.totalRaised || 0).toLocaleString()}</Td>
                    <Td>{f.totalInvestors || 0}</Td>
                    <Td className="text-green font-bold">${(f.totalReturnsPaid || 0).toFixed(2)}</Td>
                    <Td><Badge label={`${f.minMonthlyReturn}–${f.maxMonthlyReturn}%/mo`} color="purple" /></Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>
      )}

      {/* ISP PAYMENTS TAB */}
      {tab === 'isp-payments' && (
        <div className="space-y-4">
          {/* ISP Management sub-tabs */}
          <div className="flex gap-2 flex-wrap">
            {[['payments', '📡 Payments'], ['packages', '📦 Packages'], ['vouchers', '🎟 Voucher Stock'], ['activations', '🔌 Activations']].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setIspSubTab(id)}
                className={clsx('px-4 py-2 rounded-xl text-sm font-semibold transition-all', ispSubTab === id ? 'bg-cyan text-bg' : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi')}
              >{label}</button>
            ))}
          </div>
          {ispSubTab === 'payments' && (<>
          <Card>
            <div className="flex flex-wrap gap-2 items-center mb-4">
              <SectionHeader title="ISP Internet Payments" className="flex-1" />
              <select
                value={ispFilter}
                onChange={e => { setIspFilter(e.target.value); setIspPage(1); }}
                className="text-xs bg-bg-panel border border-border rounded-lg px-2 py-1 text-text-secondary"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={ispMethodFilter}
                onChange={e => { setIspMethodFilter(e.target.value); setIspPage(1); }}
                className="text-xs bg-bg-panel border border-border rounded-lg px-2 py-1 text-text-secondary"
              >
                <option value="">All Methods</option>
                <option value="dwipay_wallet">DwiPay Wallet</option>
                <option value="mtn_momo">MTN MoMo</option>
                <option value="airtel_money">Airtel Money</option>
              </select>
              <button onClick={fetchIspPayments} className="text-text-muted hover:text-cyan p-1">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            {ispLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : ispPayments.length === 0 ? (
              <Empty icon="📡" title="No ISP payments" />
            ) : (
              <Table headers={['Ref', 'Customer', 'Plan', 'Amount', 'Method', 'Payment', 'Activation', 'Callback', 'Date', 'Actions']}>
                {ispPayments.map((p) => (
                  <tr key={p._id} className="hover:bg-bg-panel/50">
                    <Td><span className="font-mono text-[10px] text-text-muted">{p.reference}</span></Td>
                    <Td>
                      <div className="text-xs">
                        <p className="font-semibold text-text-primary">{p.userId?.name || p.phone || 'Guest'}</p>
                        {p.userId?.phone && <p className="text-text-muted">{p.userId.phone}</p>}
                      </div>
                    </Td>
                    <Td>
                      <div className="text-xs">
                        <p className="text-text-primary">{p.planSnapshot?.name || '—'}</p>
                        <p className="text-text-muted">{p.planSnapshot?.durationDays}d</p>
                      </div>
                    </Td>
                    <Td><span className="text-cyan font-bold">{(p.amount || 0).toLocaleString()} UGX</span></Td>
                    <Td>
                      <Badge
                        label={p.method === 'dwipay_wallet' ? 'Wallet' : p.method === 'mtn_momo' ? 'MTN' : 'Airtel'}
                        color={p.method === 'dwipay_wallet' ? 'purple' : p.method === 'mtn_momo' ? 'yellow' : 'red'}
                      />
                    </Td>
                    <Td>
                      <Badge
                        label={p.paymentStatus}
                        color={p.paymentStatus === 'completed' ? 'green' : p.paymentStatus === 'failed' ? 'red' : 'yellow'}
                      />
                    </Td>
                    <Td>
                      <Badge
                        label={p.activationStatus}
                        color={p.activationStatus === 'activated' ? 'green' : p.activationStatus === 'failed' ? 'red' : 'yellow'}
                      />
                    </Td>
                    <Td>
                      <Badge
                        label={p.callbackStatus}
                        color={p.callbackStatus === 'sent' ? 'green' : p.callbackStatus === 'failed' ? 'red' : p.callbackStatus === 'not_required' ? 'purple' : 'yellow'}
                      />
                    </Td>
                    <Td className="text-xs text-text-muted whitespace-nowrap">
                      {p.createdAt ? format(new Date(p.createdAt), 'dd/MM HH:mm') : '—'}
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleIspViewDetail(p._id)}
                          className="text-xs text-cyan hover:underline"
                        >Detail</button>
                        {p.callbackStatus === 'failed' && (
                          <button
                            onClick={() => handleIspRetryCallback(p._id)}
                            className="text-xs text-yellow hover:underline ml-1"
                          >Retry</button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
            {ispTotal > 50 && (
              <div className="flex gap-2 justify-center mt-4">
                <Button size="sm" variant="ghost" disabled={ispPage <= 1} onClick={() => setIspPage(p => p - 1)}>Prev</Button>
                <span className="text-text-muted text-xs self-center">{ispPage} / {Math.ceil(ispTotal / 50)}</span>
                <Button size="sm" variant="ghost" disabled={ispPage >= Math.ceil(ispTotal / 50)} onClick={() => setIspPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </Card>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <SectionHeader title="Payment Endpoints" />
              <div className="space-y-2 text-xs text-text-muted font-mono">
                <p className="text-text-primary font-semibold">Captive Portal (Public)</p>
                <p>GET /api/isp/payments/plans/:routerId/public</p>
                <p>POST /api/isp/payments/mobile-money</p>
                <p className="text-text-primary font-semibold mt-2">Customer (JWT)</p>
                <p>POST /api/isp/payments/wallet</p>
                <p>GET /api/isp/payments/history</p>
                <p className="text-text-primary font-semibold mt-2">Yo! ISP IPN (no auth)</p>
                <p>POST /api/isp/payments/ipn</p>
              </div>
            </Card>
            <Card>
              <SectionHeader title="Centipede Callback" />
              <div className="space-y-2 text-xs text-text-muted">
                <p>After each successful payment, DwiPay POSTs to the Centipede <code>callbackUrl</code> with:</p>
                <div className="bg-bg rounded-lg p-2 font-mono text-[10px] text-text-secondary">
                  {`{\n  event: "payment.completed",\n  reference: "ISP-...",\n  customerRef: "mac/session",\n  plan: { durationDays, downloadSpeed, ... },\n  status: "success"\n}`}
                </div>
                <p>Header: <code>X-DWI-Signature: hmac-sha256</code> (set ISP_CALLBACK_SECRET env)</p>
                <p>Failed callbacks are retried via the Retry button above.</p>
              </div>
            </Card>
          </div>
          </>)}
          {ispSubTab === 'packages' && <Card><IspPackagesAdminTab /></Card>}
          {ispSubTab === 'vouchers' && <Card><IspVoucherStockAdminTab /></Card>}
          {ispSubTab === 'activations' && <Card><IspActivationsAdminTab /></Card>}
        </div>
      )}

      {/* ISP PAYMENT DETAIL MODAL */}
      <Modal
        open={!!ispDetailModal}
        onClose={() => setIspDetailModal(null)}
        title={`ISP Payment — ${ispDetailModal?.reference || ''}`}
        size="lg"
      >
        {ispDetailModal && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-bg-panel border border-border rounded-xl p-3 space-y-1">
                <p className="text-text-muted text-xs">Customer</p>
                <p className="font-semibold">{ispDetailModal.userId?.name || ispDetailModal.phone || 'Guest'}</p>
                {ispDetailModal.userId?.email && <p className="text-text-muted text-xs">{ispDetailModal.userId.email}</p>}
              </div>
              <div className="bg-bg-panel border border-border rounded-xl p-3 space-y-1">
                <p className="text-text-muted text-xs">Plan</p>
                <p className="font-semibold">{ispDetailModal.planSnapshot?.name || '—'}</p>
                <p className="text-text-muted text-xs">{ispDetailModal.planSnapshot?.durationDays} days · {(ispDetailModal.amount || 0).toLocaleString()} UGX</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                ['Payment', ispDetailModal.paymentStatus],
                ['Activation', ispDetailModal.activationStatus],
                ['Callback', ispDetailModal.callbackStatus],
              ].map(([label, val]) => (
                <div key={label} className="bg-bg-panel border border-border rounded-xl p-2 text-center">
                  <p className="text-text-muted text-xs mb-1">{label}</p>
                  <Badge label={val} color={val === 'completed' || val === 'activated' || val === 'sent' ? 'green' : val === 'failed' ? 'red' : 'yellow'} />
                </div>
              ))}
            </div>
            {ispDetailModal.error && (
              <div className="bg-red/10 border border-red/30 rounded-xl p-2 text-xs text-red">
                Error: {ispDetailModal.error}
              </div>
            )}
            {ispDetailModal.callbackError && (
              <div className="bg-yellow/10 border border-yellow/30 rounded-xl p-2 text-xs text-yellow">
                Callback error: {ispDetailModal.callbackError}
              </div>
            )}
            {ispDetailModal.auditTrail?.length > 0 && (
              <div>
                <p className="text-text-muted text-xs mb-2 font-semibold">Audit Trail</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {ispDetailModal.auditTrail.map((entry, i) => (
                    <div key={i} className="flex gap-2 items-start text-xs bg-bg-panel/50 rounded-lg p-2">
                      <Badge label={entry.status || entry.action} color={entry.status === 'completed' || entry.status === 'activated' ? 'green' : entry.status === 'failed' ? 'red' : 'yellow'} />
                      <div>
                        <p className="text-text-secondary">{entry.action}</p>
                        <p className="text-text-muted">{entry.message}</p>
                        <p className="text-text-muted opacity-60">{entry.timestamp ? format(new Date(entry.timestamp), 'dd/MM/yy HH:mm:ss') : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {ispDetailModal.callbackStatus === 'failed' && (
              <Button
                className="w-full"
                onClick={() => { handleIspRetryCallback(ispDetailModal._id); setIspDetailModal(null); }}
              >
                Retry Centipede Callback
              </Button>
            )}
          </div>
        )}
      </Modal>

      {/* WITHDRAWALS TAB */}
      {tab === 'withdrawals' && (
        <div className="space-y-4">
          {/* Stats row */}
          {wdStats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Pending Approval', value: wdStats.byStatus?.pending_approval?.count || wdStats.pendingApproval || 0, color: 'text-yellow' },
                { label: 'Processing',       value: wdStats.byStatus?.processing?.count       || 0, color: 'text-cyan'   },
                { label: 'Paid Today',       value: wdStats.todayPaidCount                    || 0, color: 'text-green'  },
                { label: 'Flagged',          value: wdStats.flagged                           || 0, color: 'text-red'    },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-bg-panel border border-border rounded-xl p-3 text-center">
                  <p className="text-text-muted text-xs mb-1">{label}</p>
                  <p className={`text-2xl font-bold ${color}`}>{value}</p>
                </div>
              ))}
            </div>
          )}

          <Card>
            <div className="flex flex-wrap gap-2 items-center mb-4">
              <SectionHeader title="Withdrawal Queue" className="flex-1" />
              <select
                value={wdStatusFilter}
                onChange={e => { setWdStatusFilter(e.target.value); setWdPage(1); }}
                className="text-xs bg-bg-panel border border-border rounded-lg px-2 py-1 text-text-secondary"
              >
                <option value="">All Statuses</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="processing">Processing</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                value={wdMethodFilter}
                onChange={e => { setWdMethodFilter(e.target.value); setWdPage(1); }}
                className="text-xs bg-bg-panel border border-border rounded-lg px-2 py-1 text-text-secondary"
              >
                <option value="">All Methods</option>
                <option value="mtn_momo">MTN MoMo</option>
                <option value="airtel_money">Airtel Money</option>
              </select>
              <select
                value={wdFlaggedFilter}
                onChange={e => { setWdFlaggedFilter(e.target.value); setWdPage(1); }}
                className="text-xs bg-bg-panel border border-border rounded-lg px-2 py-1 text-text-secondary"
              >
                <option value="">All</option>
                <option value="true">Flagged only</option>
              </select>
              <button onClick={fetchWithdrawals} className="text-text-muted hover:text-cyan p-1">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            {wdLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : withdrawals.length === 0 ? (
              <Empty icon="💸" title="No withdrawals" />
            ) : (
              <Table headers={['Ref', 'Customer', 'Amount', 'Net', 'Method', 'Phone', 'Status', 'Flagged', 'Date', 'Actions']}>
                {withdrawals.map(w => {
                  const statusColor = { pending_approval:'yellow', approved:'cyan', processing:'cyan', paid:'green', failed:'red', refunded:'orange', cancelled:'gray' }[w.status] || 'gray';
                  return (
                    <tr key={w._id} className="border-b border-border hover:bg-bg-panel/50">
                      <Td><span className="font-mono text-[10px]">{w.reference?.slice(-8)}</span></Td>
                      <Td>{w.user?.name || '—'}<br /><span className="text-text-muted text-[10px]">{w.user?.email}</span></Td>
                      <Td><span className="text-text-primary font-semibold">{(w.amount || 0).toLocaleString()}</span><br /><span className="text-text-muted text-[10px]">UGX</span></Td>
                      <Td><span className="text-green font-semibold">{(w.netAmount || 0).toLocaleString()}</span><br /><span className="text-text-muted text-[10px]">fee {(w.fee || 0).toLocaleString()}</span></Td>
                      <Td>{w.method === 'mtn_momo' ? 'MTN' : 'Airtel'}</Td>
                      <Td><span className="font-mono text-xs">{w.phone}</span></Td>
                      <Td><Badge label={w.status} color={statusColor} /></Td>
                      <Td>{w.flagged ? <span className="text-red font-bold">⚑</span> : '—'}</Td>
                      <Td>{w.createdAt ? format(new Date(w.createdAt), 'dd/MM HH:mm') : '—'}</Td>
                      <Td>
                        <div className="flex gap-1 flex-wrap">
                          <button onClick={() => handleWdViewDetail(w._id)} className="text-[10px] text-cyan underline">Detail</button>
                          {w.status === 'pending_approval' && (
                            <>
                              <button onClick={() => handleWdAction('approve', w._id)} className="text-[10px] text-green underline">Approve</button>
                              <button onClick={() => { setWdRejectModal(w); setWdActionReason(''); }} className="text-[10px] text-red underline">Reject</button>
                            </>
                          )}
                          {w.status === 'approved' && (
                            <button onClick={() => handleWdAction('process', w._id)} className="text-[10px] text-cyan underline">Process Payout</button>
                          )}
                          {(w.status === 'failed' || w.status === 'refunded') && w.retryCount < 3 && (
                            <button onClick={() => handleWdAction('retry', w._id)} className="text-[10px] text-yellow underline">Retry</button>
                          )}
                          {(w.status === 'pending_approval' || w.status === 'approved') && (
                            <button onClick={() => { setWdCancelModal(w); setWdActionReason(''); }} className="text-[10px] text-red underline">Cancel</button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </Table>
            )}
            {wdTotal > 20 && (
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" size="sm" disabled={wdPage === 1} onClick={() => setWdPage(p => p - 1)}>Prev</Button>
                <span className="text-text-muted text-sm self-center">Page {wdPage}</span>
                <Button variant="outline" size="sm" disabled={withdrawals.length < 20} onClick={() => setWdPage(p => p + 1)}>Next</Button>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* STUCK WITHDRAWALS TAB — DEBT-005 Stage 1: detection + read-only
          display. No action buttons yet — "Detail" reuses the existing
          withdrawal detail modal/data (handleWdViewDetail / wdDetailModal)
          below, nothing new to fetch or mutate. Refund/Mark Paid land here
          in Stage 3, each requiring a Yo!-dashboard-checked note. */}
      {tab === 'stuck-withdrawals' && (
        <div className="space-y-4">
          <div className="bg-bg-panel border border-border rounded-xl p-4 text-sm text-text-secondary">
            Withdrawals stuck in <span className="font-mono text-cyan">processing</span> beyond 1 hour —
            Yo! accepted the payout but never confirmed disbursement, and this account can't poll Yo! for
            the real outcome (<span className="font-mono">acgetbulkpaymentstatus</span> isn't enabled — DEBT-004).
            Check each one on the Yo!/MNO dashboard directly before taking any action.
          </div>

          {stuckWithdrawals.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {STUCK_LEVELS.map(({ level, label, color }) => (
                <div key={level} className="bg-bg-panel border border-border rounded-xl p-3 text-center">
                  <p className="text-text-muted text-xs mb-1">{label}</p>
                  <p className={`text-2xl font-bold ${color}`}>
                    {stuckWithdrawals.filter(w => w.stuckLevel === level).length}
                  </p>
                </div>
              ))}
            </div>
          )}

          <Card>
            <div className="flex flex-wrap gap-2 items-center mb-4">
              <SectionHeader title={`Stuck Withdrawals (${stuckWithdrawals.length})`} className="flex-1" />
              <button onClick={fetchStuckWithdrawals} className="text-text-muted hover:text-cyan p-1">
                <RefreshCw className={`w-4 h-4 ${stuckLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            {stuckLoading ? (
              <div className="flex justify-center py-8"><Spinner /></div>
            ) : stuckWithdrawals.length === 0 ? (
              <Empty icon="✅" title="No stuck withdrawals" description="Every processing withdrawal is under 1 hour old." />
            ) : (
              <Table headers={['Ref', 'Account', 'Amount', 'Method', 'Destination', 'Yo! Ref', 'Status', 'Stuck', 'Actions']}>
                {stuckWithdrawals.map(w => {
                  const levelMeta = STUCK_LEVELS.find(l => l.level === w.stuckLevel);
                  return (
                    <tr key={w._id} className="border-b border-border hover:bg-bg-panel/50">
                      <Td><span className="font-mono text-[10px]">{w.reference?.slice(-8)}</span></Td>
                      <Td>{w.user?.name || '—'}<br /><span className="text-text-muted text-[10px]">{w.user?.email}</span></Td>
                      <Td><span className="text-green font-semibold">{(w.netAmount || 0).toLocaleString()}</span><br /><span className="text-text-muted text-[10px]">{w.currency}</span></Td>
                      <Td>{w.method === 'mtn_momo' ? 'MTN' : 'Airtel'}</Td>
                      <Td><span className="font-mono text-xs">{w.phone}</span></Td>
                      <Td><span className="font-mono text-[10px]">{w.providerRef || '—'}</span></Td>
                      <Td><Badge label={w.status} color="cyan" /></Td>
                      <Td><Badge label={`${w.ageHours}h`} color={levelMeta?.badgeColor || 'gray'} /></Td>
                      <Td><button onClick={() => handleWdViewDetail(w._id)} className="text-[10px] text-cyan underline">Detail</button></Td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </Card>
        </div>
      )}

      {/* WITHDRAWAL DETAIL MODAL */}
      <Modal open={!!wdDetailModal} onClose={() => setWdDetailModal(null)} title="Withdrawal Detail" size="md">
        {wdDetailModal && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs">
              {[
                ['Reference', wdDetailModal.reference],
                ['Customer',  wdDetailModal.user?.name || '—'],
                ['Amount',    `${(wdDetailModal.amount || 0).toLocaleString()} UGX`],
                ['Fee',       `${(wdDetailModal.fee || 0).toLocaleString()} UGX`],
                ['Net Amount',`${(wdDetailModal.netAmount || 0).toLocaleString()} UGX`],
                ['Method',    wdDetailModal.method],
                ['Phone',     wdDetailModal.phone],
                ['Status',    wdDetailModal.status],
                ['Provider',  wdDetailModal.provider || '—'],
                ['Provider Ref', wdDetailModal.providerRef || '—'],
                ['Retry Count', wdDetailModal.retryCount],
                ['Flagged',   wdDetailModal.flagged ? `Yes — ${wdDetailModal.flagReason}` : 'No'],
              ].map(([label, val]) => (
                <div key={label} className="bg-bg-panel border border-border rounded-lg p-2">
                  <p className="text-text-muted text-[10px]">{label}</p>
                  <p className="text-text-primary font-semibold break-all">{val}</p>
                </div>
              ))}
            </div>
            {wdDetailModal.auditTrail?.length > 0 && (
              <div>
                <p className="text-text-muted text-xs font-semibold mb-2">Audit Trail</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {wdDetailModal.auditTrail.map((e, i) => (
                    <div key={i} className="bg-bg-panel border border-border rounded-lg p-2 text-xs">
                      <span className="text-cyan font-semibold">{e.action}</span>
                      {e.status && <span className="text-text-muted ml-2">→ {e.status}</span>}
                      {e.actorName && <span className="text-text-muted ml-2">by {e.actorName}</span>}
                      {e.message && <p className="text-text-secondary mt-1">{e.message}</p>}
                      <p className="text-text-muted text-[10px]">{e.timestamp ? format(new Date(e.timestamp), 'dd/MM/yyyy HH:mm:ss') : ''}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* WITHDRAWAL REJECT MODAL */}
      <Modal open={!!wdRejectModal} onClose={() => { setWdRejectModal(null); setWdActionReason(''); }} title="Reject Withdrawal" size="sm">
        {wdRejectModal && (
          <div className="space-y-3">
            <p className="text-text-secondary text-sm">
              Rejecting <strong>{wdRejectModal.reference?.slice(-8)}</strong> for <strong>{wdRejectModal.user?.name}</strong>.
              <br />
              <span className="text-green font-semibold">{(wdRejectModal.amount || 0).toLocaleString()} UGX</span> will be refunded immediately.
            </p>
            <Input
              label="Rejection Reason (shown to customer)"
              placeholder="KYC required, suspicious activity, policy violation…"
              value={wdActionReason}
              onChange={e => setWdActionReason(e.target.value)}
            />
            <Button variant="danger" className="w-full" disabled={wdActionLoading || !wdActionReason.trim()} onClick={() => handleWdAction('reject', wdRejectModal._id, wdActionReason)}>
              Reject & Refund
            </Button>
          </div>
        )}
      </Modal>

      {/* WITHDRAWAL CANCEL MODAL */}
      <Modal open={!!wdCancelModal} onClose={() => { setWdCancelModal(null); setWdActionReason(''); }} title="Cancel Withdrawal" size="sm">
        {wdCancelModal && (
          <div className="space-y-3">
            <p className="text-text-secondary text-sm">
              Cancelling <strong>{wdCancelModal.reference?.slice(-8)}</strong>.
              <br />
              <span className="text-green font-semibold">{(wdCancelModal.amount || 0).toLocaleString()} UGX</span> will be refunded to customer wallet.
            </p>
            <Input
              label="Cancellation Reason"
              placeholder="Reason for cancellation…"
              value={wdActionReason}
              onChange={e => setWdActionReason(e.target.value)}
            />
            <Button variant="danger" className="w-full" disabled={wdActionLoading} onClick={() => handleWdAction('cancel', wdCancelModal._id, wdActionReason)}>
              Cancel & Refund
            </Button>
          </div>
        )}
      </Modal>

      {/* CRYPTO MARK SENT MODAL */}
      <Modal
        open={!!cryptoMarkSentModal}
        onClose={() => { setCryptoMarkSentModal(null); setCryptoMarkSentTxHash(''); }}
        title="Mark USDT Withdrawal as Sent"
        size="sm"
      >
        {cryptoMarkSentModal && (
          <div className="space-y-3">
            <div className="bg-bg-panel border border-border rounded-xl p-3 space-y-1 text-xs">
              <p className="text-text-muted">Customer: <strong className="text-text-primary">{cryptoMarkSentModal.user?.name}</strong></p>
              <p className="text-text-muted">Net amount: <strong className="text-green">₮ {cryptoMarkSentModal.netAmount} USDT</strong></p>
              <p className="text-text-muted">Network: <strong className="text-cyan">{cryptoMarkSentModal.network}</strong></p>
              <p className="text-text-muted">Destination wallet:</p>
              <p className="font-mono text-text-primary text-[10px] break-all">{cryptoMarkSentModal.destinationWallet}</p>
            </div>
            <Input
              label="Blockchain Transaction Hash"
              placeholder="0x... (BSC/ETH) or transaction ID (TRX)"
              value={cryptoMarkSentTxHash}
              onChange={e => setCryptoMarkSentTxHash(e.target.value)}
            />
            <Button className="w-full" disabled={!cryptoMarkSentTxHash.trim()} onClick={handleCryptoMarkSent}>
              <Send className="w-4 h-4" /> Confirm — USDT Sent to Customer
            </Button>
          </div>
        )}
      </Modal>

      {/* CRYPTO REJECT MODAL */}
      <Modal
        open={!!cryptoRejectModal}
        onClose={() => { setCryptoRejectModal(null); setCryptoRejectReason(''); }}
        title="Reject Crypto Withdrawal"
        size="sm"
      >
        {cryptoRejectModal && (
          <div className="space-y-3">
            <p className="text-text-secondary text-sm">
              Rejecting withdrawal for <strong className="text-text-primary">{cryptoRejectModal.user?.name}</strong>.
              <br />
              <span className="text-green font-semibold">₮ {cryptoRejectModal.amount} USDT</span> will be refunded to their internal balance immediately.
            </p>
            <Input
              label="Rejection Reason (shown to customer)"
              placeholder="Wallet address invalid, insufficient USDT treasury, suspicious activity, etc."
              value={cryptoRejectReason}
              onChange={e => setCryptoRejectReason(e.target.value)}
            />
            <Button variant="danger" className="w-full" onClick={handleCryptoReject}>
              Reject & Refund USDT
            </Button>
          </div>
        )}
      </Modal>

      {/* MARK EXCHANGE AS SENT MODAL */}
      <Modal
        open={!!markSentModal}
        onClose={() => { setMarkSentModal(null); setMarkSentTxHash(''); }}
        title="Mark USDT as Sent"
        size="sm"
      >
        {markSentModal && (
          <div className="space-y-3">
            <div className="bg-bg-panel border border-border rounded-xl p-3 space-y-1 text-xs">
              <p className="text-text-muted">Customer: <strong className="text-text-primary">{markSentModal.user?.name}</strong></p>
              <p className="text-text-muted">Amount: <strong className="text-green">{markSentModal.netAmount} USDT</strong></p>
              <p className="text-text-muted">Network: <strong className="text-cyan">{markSentModal.network}</strong></p>
              <p className="text-text-muted">To wallet:</p>
              <p className="font-mono text-text-primary text-[10px] break-all">{markSentModal.destinationWallet}</p>
            </div>
            <Input
              label="Blockchain Transaction Hash"
              placeholder="0x... or txid..."
              value={markSentTxHash}
              onChange={e => setMarkSentTxHash(e.target.value)}
            />
            <Button className="w-full" disabled={!markSentTxHash.trim()} onClick={handleMarkSent}>
              <Send className="w-4 h-4" /> Confirm — USDT Sent
            </Button>
          </div>
        )}
      </Modal>

      {/* REJECT EXCHANGE MODAL */}
      <Modal
        open={!!rejectExchangeModal}
        onClose={() => { setRejectExchangeModal(null); setRejectExchangeReason(''); }}
        title="Reject Exchange Order"
        size="sm"
      >
        {rejectExchangeModal && (
          <div className="space-y-3">
            <p className="text-text-secondary text-sm">
              Rejecting order for <strong className="text-text-primary">{rejectExchangeModal.user?.name}</strong>.
              <br />
              <span className="text-green font-semibold">{rejectExchangeModal.fromAmount?.toLocaleString()} UGX</span> will be refunded immediately.
            </p>
            <Input
              label="Rejection Reason (shown to customer)"
              placeholder="Wallet address invalid, KYC required, suspicious activity, etc."
              value={rejectExchangeReason}
              onChange={e => setRejectExchangeReason(e.target.value)}
            />
            <Button variant="danger" className="w-full" onClick={handleRejectExchange}>
              Reject & Refund UGX
            </Button>
          </div>
        )}
      </Modal>


      {/* MERCHANTS TAB */}
      {tab === 'merchants' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <SectionHeader title="Merchant Registry" className="flex-1" />
            <select
              value={merchantFilter}
              onChange={e => { setMerchantFilter(e.target.value); }}
              className="text-xs bg-bg-panel border border-border rounded-lg px-2 py-1 text-text-secondary"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
            <Button size="sm" onClick={fetchMerchants}><RefreshCw className="w-3.5 h-3.5" /></Button>
            <Button size="sm" onClick={() => setRegisterModal(true)}>+ Register Merchant</Button>
          </div>

          <Card>
            {merchantLoading ? (
              <div className="flex justify-center py-12"><Spinner size="lg" /></div>
            ) : merchants.length === 0 ? (
              <Empty icon="🏪" title="No merchants" description="Register a merchant to get started" />
            ) : (
              <Table headers={['Business', 'Code', 'Status', 'Balance', 'Revenue', 'Actions']}>
                {merchants.map((m) => (
                  <tr key={m._id} className="border-b border-border/30 hover:bg-bg-panel/40 transition-colors">
                    <Td>
                      <div>
                        <p className="text-text-primary font-semibold text-sm">{m.businessName}</p>
                        <p className="text-text-muted text-xs">{m.userId?.email || m.userId?.phone || '—'}</p>
                      </div>
                    </Td>
                    <Td><span className="font-mono text-xs text-cyan">{m.code}</span></Td>
                    <Td>
                      <Badge
                        label={m.status}
                        color={m.status === 'active' ? 'green' : m.status === 'suspended' ? 'red' : 'amber'}
                      />
                    </Td>
                    <Td>
                      <span className="text-text-primary font-bold text-sm">
                        {(m.walletId?.balance ?? 0).toLocaleString()} UGX
                      </span>
                    </Td>
                    <Td>
                      <span className="text-green text-sm font-semibold">
                        {(m.totalRevenue ?? 0).toLocaleString()} UGX
                      </span>
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        <Button size="xs" variant="ghost" onClick={() => openMerchantDetail(m)}>View</Button>
                        {m.status !== 'active' && (
                          <Button size="xs" onClick={() => handleMerchantStatus(m._id, 'active')}>Activate</Button>
                        )}
                        {m.status === 'active' && (
                          <Button size="xs" variant="danger" onClick={() => handleMerchantStatus(m._id, 'suspended')}>Suspend</Button>
                        )}
                        <Button size="xs" variant="secondary" onClick={() => handleMerchantSettle(m._id)}>Settle</Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>
      )}

      {/* REJECT KYC MODAL */}
      <Modal open={!!rejectModal} onClose={() => { setRejectModal(null); setRejectReason(''); setRejectReasonOther(''); }} title="Reject KYC" size="sm">
        {rejectModal && (
          <div className="space-y-3">
            <p className="text-text-secondary text-sm">Rejecting KYC for <strong className="text-text-primary">{rejectModal.name}</strong></p>
            <div>
              <label className="block text-text-secondary text-xs font-medium mb-1">Rejection Reason</label>
              <select
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2 text-text-primary text-xs outline-none focus:border-cyan"
              >
                <option value="">Select reason...</option>
                {KYC_REJECT_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            {rejectReason === 'Other (specify below)' && (
              <Input
                label="Specify reason"
                placeholder="Describe the issue clearly..."
                value={rejectReasonOther}
                onChange={(e) => setRejectReasonOther(e.target.value)}
              />
            )}
            <Button variant="danger" className="w-full" disabled={!rejectReason} onClick={handleRejectKYC}>
              Confirm Rejection
            </Button>
          </div>
        )}
      </Modal>

      {/* SUSPEND MODAL */}
      <Modal open={!!suspendModal} onClose={() => { setSuspendModal(null); setSuspendReason(''); }} title="Suspend User" size="sm">
        {suspendModal && (
          <div className="space-y-3">
            <p className="text-text-secondary text-sm">Suspending <strong className="text-text-primary">{suspendModal.name}</strong></p>
            <Input
              label="Suspension Reason"
              placeholder="Fraudulent activity, policy violation, etc."
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
            />
            <Button variant="danger" className="w-full" onClick={handleSuspend}>
              Suspend Account
            </Button>
          </div>
        )}
      </Modal>

      {/* RESET PASSWORD MODAL — help-desk tool for locked-out customers */}
      <Modal open={!!resetPwModal} onClose={() => { setResetPwModal(null); setResetPwValue(''); }} title="Reset User Password" size="sm">
        {resetPwModal && (
          <div className="space-y-3">
            <p className="text-text-secondary text-sm">
              Setting a new temporary password for <strong className="text-text-primary">{resetPwModal.name}</strong> ({resetPwModal.phone})
            </p>
            <Input
              type="text"
              label="New Temporary Password"
              placeholder="At least 6 characters"
              value={resetPwValue}
              onChange={(e) => setResetPwValue(e.target.value)}
              note="Not sent automatically — relay it to the customer yourself (call/SMS) once saved."
            />
            <Button variant="primary" className="w-full" onClick={handleResetPassword}>
              Reset Password
            </Button>
          </div>
        )}
      </Modal>

      {/* MERCHANT DETAIL MODAL */}
      <Modal open={!!merchantDetail} onClose={() => { setMerchantDetail(null); setMerchantPayments([]); }} title="Merchant Detail" size="lg">
        {merchantDetail && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Business',    merchantDetail.businessName],
                ['Code',        merchantDetail.code],
                ['Status',      merchantDetail.status],
                ['Fee %',       `${merchantDetail.paymentFeePercent}%`],
                ['Settlement',  merchantDetail.settlementFrequency],
                ['Phone',       merchantDetail.settlementPhone || merchantDetail.phone || '—'],
                ['Payments',    merchantDetail.totalPaymentsReceived ?? 0],
                ['Revenue',     `${(merchantDetail.totalRevenue ?? 0).toLocaleString()} UGX`],
              ].map(([k, v]) => (
                <div key={k} className="bg-bg-panel rounded-lg p-2">
                  <p className="text-text-muted text-xs">{k}</p>
                  <p className="text-text-primary font-semibold">{v}</p>
                </div>
              ))}
            </div>
            {merchantPayments.length > 0 && (
              <div>
                <p className="text-text-muted text-xs mb-2 font-semibold uppercase tracking-wider">Recent Payments</p>
                <Table headers={['Amount', 'Status', 'Method', 'Date']}>
                  {merchantPayments.map(p => (
                    <tr key={p._id} className="border-b border-border/30">
                      <Td><span className="font-bold">{(p.amount ?? 0).toLocaleString()} {p.currency}</span></Td>
                      <Td><Badge label={p.status} color={p.status === 'completed' ? 'green' : p.status === 'failed' ? 'red' : 'amber'} /></Td>
                      <Td>{p.method}</Td>
                      <Td>{p.createdAt ? format(new Date(p.createdAt), 'MMM d, HH:mm') : '—'}</Td>
                    </tr>
                  ))}
                </Table>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* FEATURE FLAGS (Phase 20) */}
      {tab === 'features' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-black text-text-primary">Production Launch Controls</h2>
            <p className="text-text-muted text-sm mt-1">Toggle feature visibility without redeploying. Admins always bypass all gates.</p>
          </div>
          <ClientMenuSwitches />

          {adminFeaturesLoading ? <Spinner size="lg" /> : (
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(adminFeatures).map(([key, flag]) => {
                const visColor = flag.visibility === 'public' ? '#10B981' : flag.visibility === 'coming_soon' ? '#F59E0B' : '#EF4444';
                const visLabel = flag.visibility === 'public' ? '?? Public' : flag.visibility === 'coming_soon' ? '?? Coming Soon' : '?? Private';
                return (
                  <Card key={key}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-text-primary text-sm">{flag.label}</p>
                          <span className="text-xs font-bold" style={{ color: visColor }}>{visLabel}</span>
                        </div>
                        <p className="text-text-muted text-xs mt-0.5">{flag.description}</p>
                        <p className="text-text-muted text-xs font-mono mt-1 opacity-60">{key}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {['public', 'coming_soon', 'private'].map(v => (
                        <button key={v}
                          onClick={() => updateAdminFeature(key, v)}
                          className={"px-3 py-1 rounded-lg text-xs font-bold border transition-all " + (
                            flag.visibility === v
                              ? v === 'public' ? 'bg-green-500/20 border-green-500/40 text-green-400'
                              : v === 'coming_soon' ? 'bg-amber-500/20 border-amber-500/40 text-amber-400'
                              : 'bg-red-500/20 border-red-500/40 text-red-400'
                              : 'bg-bg-panel border-border text-text-muted hover:border-border-hi'
                          )}>
                          {v === 'coming_soon' ? 'Soon' : v.charAt(0).toUpperCase() + v.slice(1)}
                        </button>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="bg-bg-panel border border-border rounded-xl p-4">
            <p className="text-text-muted text-xs leading-relaxed">
              <span className="font-bold text-green-400">Public</span> ? visible and accessible to all users. &nbsp;
              <span className="font-bold text-amber-400">Coming Soon</span> ? visible in nav with &quot;SOON&quot; badge, clicking shows Coming Soon page; API returns 403. &nbsp;
              <span className="font-bold text-red-400">Private</span> ? hidden from nav, API returns 403. Admins bypass all states.
            </p>
          </div>
        </div>
      )}

      {/* INVESTMENT PROJECTS (Phase 19) */}
      {tab === 'projects' && (
        <div className="space-y-6">
          <h2 className="text-lg font-black text-text-primary">DWI Investment Projects</h2>

          {/* Create project form */}
          <Card>
            <p className="text-sm font-bold text-text-primary mb-4">Create New Project</p>
            <div className="grid md:grid-cols-3 gap-3">
              <Input label="Project Name" value={iProjForm.name} onChange={e => setIProjForm(p=>({...p,name:e.target.value}))} />
              <Input label="Tagline" value={iProjForm.tagline} onChange={e => setIProjForm(p=>({...p,tagline:e.target.value}))} />
              <div>
                <label className="text-xs text-text-muted mb-1 block">Category</label>
                <select className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2 text-sm text-text-primary"
                  value={iProjForm.category} onChange={e => setIProjForm(p=>({...p,category:e.target.value}))}>
                  {['internet','real_estate','agriculture','energy','manufacturing','technology','other'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <Input label="Description" value={iProjForm.description} onChange={e => setIProjForm(p=>({...p,description:e.target.value}))} />
              <div>
                <label className="text-xs text-text-muted mb-1 block">Currency</label>
                <select className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2 text-sm text-text-primary"
                  value={iProjForm.currency} onChange={e => setIProjForm(p=>({...p,currency:e.target.value}))}>
                  {['UGX','USD','USDT'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <Input label="Funding Target" type="number" value={iProjForm.fundingTarget} onChange={e => setIProjForm(p=>({...p,fundingTarget:e.target.value}))} />
              <Input label="Minimum Investment" type="number" value={iProjForm.minimumInvestment} onChange={e => setIProjForm(p=>({...p,minimumInvestment:e.target.value}))} />
              <Input label="Total Shares" type="number" value={iProjForm.totalShares} onChange={e => setIProjForm(p=>({...p,totalShares:e.target.value}))} />
              <Input label="Share Price" type="number" value={iProjForm.sharePrice} onChange={e => setIProjForm(p=>({...p,sharePrice:e.target.value}))} />
              <Input label="Est. Annual ROI %" type="number" value={iProjForm.expectedAnnualROIPercent} onChange={e => setIProjForm(p=>({...p,expectedAnnualROIPercent:e.target.value}))} />
              <div>
                <label className="text-xs text-text-muted mb-1 block">Payout Cycle</label>
                <select className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2 text-sm text-text-primary"
                  value={iProjForm.expectedPayoutCycle} onChange={e => setIProjForm(p=>({...p,expectedPayoutCycle:e.target.value}))}>
                  {['monthly','quarterly','annual','on_exit'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-text-muted mb-1 block">Status</label>
                <select className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2 text-sm text-text-primary"
                  value={iProjForm.status} onChange={e => setIProjForm(p=>({...p,status:e.target.value}))}>
                  {['planning','fundraising','construction','launch','operational','completed','suspended'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 mt-5">
                <input type="checkbox" id="isPublished" checked={iProjForm.isPublished} onChange={e => setIProjForm(p=>({...p,isPublished:e.target.checked}))} className="w-4 h-4" />
                <label htmlFor="isPublished" className="text-sm text-text-secondary">Publish immediately</label>
              </div>
            </div>
            <Button className="mt-4" loading={iProjCreateLoading} onClick={handleCreateIProject}>Create Project</Button>
          </Card>

          {/* Project list */}
          {iProjectsLoading ? <Spinner size="lg" /> : iProjects.length === 0 ? (
            <Empty icon="???" title="No projects" description="Create your first investment project above." />
          ) : (
            <div className="space-y-3">
              {iProjects.map(p => {
                const pct = p.totalShares > 0 ? +((p.sharesIssued / p.totalShares) * 100).toFixed(1) : 0;
                return (
                  <Card key={p._id} className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-black text-text-primary">{p.name}</p>
                        <span className="text-xs text-text-muted">#{p.projectCode}</span>
                        <Badge label={p.status} color={p.status === 'fundraising' ? 'cyan' : p.status === 'operational' ? 'green' : 'amber'} />
                        <Badge label={p.isPublished ? 'Published' : 'Draft'} color={p.isPublished ? 'green' : 'amber'} />
                      </div>
                      <p className="text-text-muted text-xs mt-1">
                        {p.investorCount || 0} investors ? {pct}% funded ? {(p.amountRaised || 0).toLocaleString()} {p.currency} raised
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="secondary" onClick={() => toggleIProjectPublished(p)}>
                        {p.isPublished ? 'Unpublish' : 'Publish'}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* REGISTER MERCHANT MODAL */}
      <Modal open={registerModal} onClose={() => setRegisterModal(false)} title="Register Merchant">
        <div className="space-y-3">
          {/* User search — resolves name/email/phone to _id */}
          <div className="relative">
            <label className="text-xs font-medium text-text-secondary block mb-1">Find User</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name, email or phone…"
                value={regUserQuery}
                onChange={e => handleRegUserSearch(e.target.value)}
                className="w-full bg-bg-panel border border-border rounded-xl pl-8 pr-3 py-2 text-text-primary text-xs outline-none focus:border-cyan"
              />
              {regUserSearching && <Spinner size="xs" className="absolute right-3 top-1/2 -translate-y-1/2" />}
            </div>
            {regUserResults.length > 0 && (
              <div className="absolute z-50 left-0 right-0 mt-1 bg-bg-panel border border-border rounded-xl shadow-xl overflow-hidden">
                {regUserResults.map(u => (
                  <button
                    key={u._id}
                    onClick={() => selectRegUser(u)}
                    className="w-full text-left px-3 py-2 hover:bg-bg-surface transition-colors"
                  >
                    <p className="text-text-primary text-xs font-semibold">{u.name}</p>
                    <p className="text-text-muted text-xs">{u.email} · {u.phone}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          {regSelectedUser && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-500/10 border border-green-500/20">
              <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-text-primary text-xs font-semibold truncate">{regSelectedUser.name}</p>
                <p className="text-text-muted text-xs font-mono truncate">{regSelectedUser._id}</p>
              </div>
              <button onClick={() => { setRegSelectedUser(null); setRegUserQuery(''); setRegUserResults([]); setRegForm(p => ({...p, userId: ''})); }} className="text-text-muted hover:text-red-400 ml-auto flex-shrink-0 text-xs">✕</button>
            </div>
          )}
          {/* Raw ID fallback */}
          <details className="text-xs">
            <summary className="text-text-muted cursor-pointer select-none hover:text-text-secondary">Or paste MongoDB _id directly</summary>
            <div className="mt-2">
              <Input placeholder="MongoDB user _id" value={regSelectedUser ? '' : regForm.userId} onChange={e => { setRegSelectedUser(null); setRegUserQuery(''); setRegForm(p => ({...p, userId: e.target.value})); }} />
            </div>
          </details>
          <Input label="Business Name" placeholder="Acme Ltd" value={regForm.businessName} onChange={e => setRegForm(p => ({...p, businessName: e.target.value}))} />
          <div>
            <label className="text-xs font-medium text-text-secondary block mb-1">Business Type</label>
            <select
              value={regForm.businessType}
              onChange={e => setRegForm(p => ({...p, businessType: e.target.value}))}
              className="w-full bg-bg-panel border border-border rounded-xl px-3 py-2 text-text-primary text-xs outline-none focus:border-cyan"
            >
              {['retail','restaurant','services','online','healthcare','education','transport','other'].map(t => (
                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
              ))}
            </select>
          </div>
          <Input label="Phone" placeholder="+256..." value={regForm.phone} onChange={e => setRegForm(p => ({...p, phone: e.target.value}))} />
          <Input label="Email" placeholder="biz@example.com" value={regForm.email} onChange={e => setRegForm(p => ({...p, email: e.target.value}))} />
          <Input label="Payment Fee %" type="number" value={regForm.paymentFeePercent} onChange={e => setRegForm(p => ({...p, paymentFeePercent: parseFloat(e.target.value) || 1.5}))} />
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setRegisterModal(false)}>Cancel</Button>
            <Button className="flex-1" loading={regLoading} onClick={handleRegisterMerchant}>Register</Button>
          </div>
        </div>
      </Modal>


      {/* FEE RULES TAB */}
      {tab === 'fees' && (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <SectionHeader title="Fee Rule Management" />
              <Button size="sm" onClick={fetchFeeRules} loading={feeRulesLoading}><RefreshCw size={14}/> Refresh</Button>
            </div>
            <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(0,168,219,0.08)', border: '1px solid rgba(0,168,219,0.20)' }}>
              <p className="text-xs font-bold text-cyan mb-1">Yo! UGX Withdrawal Tiers</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                {[['500 – 60,000 UGX','500 UGX'],['60,001 – 500,000 UGX','800 UGX'],['500,001 – 1,000,000 UGX','1,200 UGX'],['Above 1,000,000 UGX','1,500 UGX']].map(([range, fee]) => (
                  <div key={range} className="text-center p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <p className="text-text-muted text-xs mb-1">{range}</p>
                    <p className="text-cyan font-black text-sm">{fee}</p>
                  </div>
                ))}
              </div>
            </div>
            {feeRulesLoading ? <Spinner /> : (
              <Table headers={['Name','Code','Type','Tx Type','Fee','Currency','Status','Actions']}>
                {feeRules.map(r => (
                  <tr key={r._id} className="border-b border-border hover:bg-bg-panel/50 transition-colors">
                    <Td><span className="font-semibold text-text-primary">{r.name}</span></Td>
                    <Td><code className="text-cyan text-xs bg-cyan/10 px-2 py-0.5 rounded">{r.code}</code></Td>
                    <Td><Badge variant={r.feeType === 'tiered' ? 'blue' : 'default'}>{r.feeType}</Badge></Td>
                    <Td><span className="text-text-secondary text-xs">{r.transactionType}</span></Td>
                    <Td>
                      {r.feeType === 'fixed' && r.fixedAmount === 0 ? <span className="text-green font-bold text-xs">FREE</span>
                       : r.feeType === 'fixed' ? <span className="text-amber font-bold">{r.fixedAmount} {r.currency || ''}</span>
                       : r.feeType === 'tiered' ? <span className="text-cyan text-xs">Tiered ({r.tiers?.length} tiers)</span>
                       : <span className="text-text-secondary text-xs">{(r.rate * 100).toFixed(1)}%</span>}
                    </Td>
                    <Td>{r.applicableCurrency || r.currency || 'Any'}</Td>
                    <Td><Badge variant={r.isActive ? 'success' : 'error'}>{r.isActive ? 'Active' : 'Inactive'}</Badge></Td>
                    <Td>
                      <div className="flex gap-2">
                        <button
                          className="text-xs text-cyan hover:underline"
                          onClick={() => { setFeeEditRule(r); setFeeEditForm({ fixedAmount: r.fixedAmount, rate: r.rate, isActive: r.isActive }); setFeeEditModal(true); }}
                        >Edit</button>
                        <button
                          className={r.isActive ? 'text-xs text-red hover:underline' : 'text-xs text-green hover:underline'}
                          onClick={async () => {
                            try {
                              await feeAPI.update(r._id, { isActive: !r.isActive });
                              toast.success(`Rule ${r.isActive ? 'deactivated' : 'activated'}`);
                              fetchFeeRules();
                            } catch { toast.error('Failed to update rule'); }
                          }}
                        >{r.isActive ? 'Deactivate' : 'Activate'}</button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
        </div>
      )}

      {/* FEE EDIT MODAL */}
      <Modal open={feeEditModal} onClose={() => setFeeEditModal(false)} title={`Edit: ${feeEditRule?.name}`} size="sm">
        <div className="space-y-4">
          <p className="text-text-muted text-xs">Code: <code className="text-cyan">{feeEditRule?.code}</code> · Type: {feeEditRule?.feeType}</p>
          {feeEditRule?.feeType === 'fixed' && (
            <Input label="Fixed Amount" type="number" value={feeEditForm.fixedAmount ?? ''} onChange={e => setFeeEditForm(f => ({...f, fixedAmount: e.target.value}))} note="0 = FREE" />
          )}
          {feeEditRule?.feeType === 'percentage' && (
            <Input label="Rate (e.g. 0.01 = 1%)" type="number" value={feeEditForm.rate ?? ''} onChange={e => setFeeEditForm(f => ({...f, rate: e.target.value}))} />
          )}
          {feeEditRule?.feeType === 'tiered' && (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(0,168,219,0.06)', border: '1px solid rgba(0,168,219,0.20)' }}>
              <p className="text-cyan text-xs font-bold mb-2">Tiers (edit in MongoDB for full control)</p>
              {feeEditRule?.tiers?.map((t, i) => (
                <div key={i} className="flex justify-between text-xs text-text-secondary py-1 border-b border-border last:border-0">
                  <span>{t.minAmount?.toLocaleString()} – {t.maxAmount ? t.maxAmount.toLocaleString() : '∞'} UGX</span>
                  <span className="text-cyan font-bold">{t.value?.toLocaleString()} UGX</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3">
            <label className="text-text-secondary text-sm">Active</label>
            <button
              onClick={() => setFeeEditForm(f => ({...f, isActive: !f.isActive}))}
              className={clsx('w-12 h-6 rounded-full transition-colors relative', feeEditForm.isActive !== false ? 'bg-cyan' : 'bg-border')}
            >
              <span className={clsx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all', feeEditForm.isActive !== false ? 'left-6' : 'left-0.5')} />
            </button>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setFeeEditModal(false)}>Cancel</Button>
            <Button className="flex-1" onClick={async () => {
              try {
                const payload = { isActive: feeEditForm.isActive !== false };
                if (feeEditRule.feeType === 'fixed') payload.fixedAmount = parseFloat(feeEditForm.fixedAmount) || 0;
                if (feeEditRule.feeType === 'percentage') payload.rate = parseFloat(feeEditForm.rate) || 0;
                await feeAPI.update(feeEditRule._id, payload);
                toast.success('Fee rule updated');
                setFeeEditModal(false);
                fetchFeeRules();
              } catch { toast.error('Update failed'); }
            }}>Save Changes</Button>
          </div>
        </div>
      </Modal>

      {/* CRYPTO WALLETS TAB */}
      {tab === 'crypto-wallets' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <SectionHeader title="Crypto Networks & Wallet Addresses" />
            <div className="flex gap-2">
              <Button size="sm" onClick={fetchCryptoNets} loading={cryptoNetsLoading}><RefreshCw size={14}/> Refresh</Button>
              <Button size="sm" onClick={() => { setCryptoNetEdit(null); setCryptoNetForm({ code:'', name:'', currency:'', symbol:'', minDeposit:10, depositFee:0, withdrawalFee:1, confirmationsRequired:20, explorerTxUrl:'', explorerAddressUrl:'', notes:'' }); setCryptoNetModal(true); }}>+ Add Network</Button>
            </div>
          </div>
          {cryptoNetsLoading ? <Spinner /> : cryptoNets.length === 0 ? <Empty label="No networks configured" /> : (
            <div className="space-y-4">
              {cryptoNets.map(net => (
                <Card key={net._id}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{ width:36, height:36, borderRadius:8, background: net.logoColor || '#00A8DB', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:14 }}>
                        {net.symbol || net.code?.[0]}
                      </div>
                      <div>
                        <p className="text-text-primary font-bold text-sm">{net.name}</p>
                        <p className="text-text-muted text-xs">{net.code} · {net.currency} · Min: {net.minDeposit}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={net.isEnabled ? 'success' : 'danger'}>{net.isEnabled ? 'ENABLED' : 'DISABLED'}</Badge>
                      <Button size="sm" onClick={() => { setCryptoNetEdit(net); setCryptoNetForm({ code: net.code, name: net.name, currency: net.currency, symbol: net.symbol||'', minDeposit: net.minDeposit, depositFee: net.depositFee, withdrawalFee: net.withdrawalFee, confirmationsRequired: net.confirmationsRequired, explorerTxUrl: net.explorerTxUrl||'', explorerAddressUrl: net.explorerAddressUrl||'', isEnabled: net.isEnabled, notes: net.notes||'' }); setCryptoNetModal(true); }}>Edit</Button>
                      <Button size="sm" onClick={async () => { try { await cryptoNetworksAPI.update(net._id, { isEnabled: !net.isEnabled }); toast.success(net.isEnabled ? 'Network disabled' : 'Network enabled'); fetchCryptoNets(); } catch { toast.error('Update failed'); } }}>{net.isEnabled ? 'Disable' : 'Enable'}</Button>
                    </div>
                  </div>
                  <div className="border-t border-border pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-text-muted text-xs font-semibold uppercase">Wallet Addresses</p>
                      <Button size="sm" onClick={async () => { await fetchCryptoNetsWallets(net.code); setCryptoWalletNet(net); setCryptoWalletForm({ address:'', label:'Primary Wallet', walletType:'hot' }); setCryptoWalletModal(true); }}>+ Add Address</Button>
                    </div>
                    {(!net.wallets || net.wallets.length === 0) ? (
                      <p className="text-text-muted text-xs italic">No wallet addresses configured. Add one to enable deposits on this network.</p>
                    ) : (
                      <div className="space-y-2">
                        {(net.wallets || []).map(w => (
                          <div key={w._id} className="flex items-center gap-2 bg-bg-surface rounded-lg p-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-text-primary text-xs font-mono truncate">{w.address}</p>
                              <p className="text-text-muted text-xs">{w.label} · {w.walletType}{w.isPrimary ? ' · PRIMARY' : ''}</p>
                            </div>
                            <Badge variant={w.isActive ? 'success' : 'default'}>{w.isActive ? 'ACTIVE' : 'INACTIVE'}</Badge>
                            {!w.isPrimary && <Button size="sm" onClick={async () => { try { await cryptoNetworksAPI.activateWallet(w._id); toast.success('Set as primary'); fetchCryptoNets(); } catch { toast.error('Failed'); } }}>Set Primary</Button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CRYPTO DEPOSITS TAB */}
      {tab === 'crypto-deposits' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <SectionHeader title="Crypto Deposit Verification Queue" />
            <Button size="sm" onClick={fetchCryptoDeposits} loading={cryptoDepositsLoading}><RefreshCw size={14}/> Refresh</Button>
          </div>
          {cryptoDepositsLoading ? <Spinner /> : cryptoDeposits.length === 0 ? <Empty label="No deposit requests" /> : (
            <div className="space-y-3">
              {cryptoDeposits.map(dep => (
                <Card key={dep._id}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={dep.status === 'confirmed' ? 'success' : dep.status === 'rejected' ? 'danger' : dep.status === 'awaiting_verification' ? 'warning' : 'default'}>{dep.status.replace('_',' ').toUpperCase()}</Badge>
                        <span className="text-text-muted text-xs">{dep.requestId}</span>
                      </div>
                      <p className="text-text-primary text-sm font-semibold">{dep.networkName} · {dep.expectedAmount ? dep.expectedAmount + ' ' + dep.currency : 'Amount TBC'}</p>
                      <p className="text-text-muted text-xs">{dep.depositAddress}</p>
                      {dep.txid && <p className="text-cyan text-xs font-mono mt-1 truncate">TXID: {dep.txid}</p>}
                      <p className="text-text-muted text-xs mt-1">{new Date(dep.createdAt).toLocaleString()}</p>
                    </div>
                    <Button size="sm" onClick={() => { setCryptoDepDetail(dep); setCryptoCreditAmount(dep.expectedAmount ? String(dep.expectedAmount) : ''); setCryptoRejectReason(''); setCryptoMoreInfoNote(''); setCryptoDepModal(true); }}>Review</Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* LAUNCH EVENT TAB */}
      {tab === 'launch-event' && <Card><LaunchEventAdminTab /></Card>}
      {tab === 'partnership-network' && <PartnershipAdminTab />}
      {tab === 'network' && <NetworkAdminTab />}
      {tab === 'market' && <MarketAdminTab />}

      {/* EXCHANGE SETTINGS TAB */}
      {tab === 'exchange-settings' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <SectionHeader title="Exchange Engine Settings" />
            <Button size="sm" onClick={fetchExSettings} loading={exLoading}><RefreshCw size={14}/> Refresh</Button>
          </div>

          {/* Desk Control — independent buying/selling toggles for the manual USDT/P2P treasury desk */}
          <Card>
            <div className="flex items-center justify-between mb-1">
              <SectionHeader title="Desk Control" />
              <Button size="sm" variant="secondary" onClick={fetchDeskState} loading={deskLoading}><RefreshCw size={14}/> Refresh</Button>
            </div>
            <p className="text-text-muted text-xs mb-4">
              Admin/system only — never shown to partners or customers. Each direction is independent; toggling one
              off does not cancel or touch any already-pending order in that direction.
            </p>

            {deskLoading || !deskState ? <Spinner /> : (
              <>
                <div className="mb-4">
                  <span className={`inline-block text-xs font-bold px-3 py-1.5 rounded-lg ${
                    deskState.status === 'both' ? 'bg-green-500/15 text-green-500' :
                    deskState.status === 'closed' ? 'bg-red-500/15 text-red-500' :
                    'bg-amber-500/15 text-amber-500'
                  }`}>
                    Desk: {
                      deskState.status === 'both' ? 'OPEN (both directions)' :
                      deskState.status === 'buying-only' ? 'BUY-ONLY' :
                      deskState.status === 'selling-only' ? 'SELL-ONLY' :
                      'CLOSED'
                    }
                  </span>
                  {!deskState.masterEnabled && (
                    <p className="text-red-500 text-xs mt-2">
                      The overall Exchange Enabled master switch (below) is OFF — the desk is fully closed regardless
                      of the two toggles here. Turn that back on first if you want either direction to work.
                    </p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-text-primary text-sm font-semibold">Buying USDT</p>
                      <Button
                        size="sm"
                        variant={deskState.buying.enabled ? 'outline' : 'danger'}
                        loading={deskToggling.buying}
                        onClick={() => toggleDesk('buying', !deskState.buying.enabled)}
                      >
                        {deskState.buying.enabled ? '✅ ON — turn OFF' : '🔴 OFF — turn ON'}
                      </Button>
                    </div>
                    <p className="text-text-muted text-[11px]">
                      DWI buys USDT — customer sells USDT to DWI, DWI pays UGX. Drains UGX, grows USDT.
                    </p>
                  </div>

                  <div className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-text-primary text-sm font-semibold">Selling USDT</p>
                      <Button
                        size="sm"
                        variant={deskState.selling.enabled ? 'outline' : 'danger'}
                        loading={deskToggling.selling}
                        onClick={() => toggleDesk('selling', !deskState.selling.enabled)}
                      >
                        {deskState.selling.enabled ? '✅ ON — turn OFF' : '🔴 OFF — turn ON'}
                      </Button>
                    </div>
                    <p className="text-text-muted text-[11px]">
                      DWI sells USDT — customer buys USDT from DWI, pays UGX. Grows UGX, drains USDT.
                    </p>
                  </div>
                </div>
              </>
            )}
          </Card>

          {exLoading ? <Spinner /> : (
            <div className="grid lg:grid-cols-2 gap-4">

              {/* Buy Settings */}
              <Card>
                <SectionHeader title="UGX → USDT Buy Settings" />
                <p className="text-text-muted text-xs mb-4">The buy spread is DWI FINTECH trading margin. No additional fees are charged on buy.</p>
                <div className="space-y-3">
                  {[
                    { key: 'EXCHANGE_BUY_SPREAD',    label: 'Buy Spread (fraction)',  hint: '0.02 = 2%' },
                    { key: 'EXCHANGE_MIN_UGX',       label: 'Minimum Buy (UGX)',      hint: 'e.g. 5000' },
                    { key: 'EXCHANGE_MAX_UGX',       label: 'Maximum Buy (UGX)',      hint: 'e.g. 50000000' },
                    // EXCHANGE_ENABLED is the pre-existing overall master
                    // switch (distinct from the two new direction-specific
                    // toggles above in Desk Control) — left exactly as it
                    // was, unrelated to this change.
                    { key: 'EXCHANGE_ENABLED',       label: 'Exchange Enabled (master switch — overrides both desk directions)', hint: 'true / false' },
                    { key: 'EXCHANGE_INSTANT_CREDIT',label: 'Instant USDT Credit',    hint: 'true = instant' },
                  ].map(({ key, label, hint }) => {
                    const entry = exSettings.find(e => e.key === key);
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-text-primary text-xs font-semibold">{label}</p>
                          <p className="text-text-muted text-[10px]">{hint}</p>
                        </div>
                        <input
                          className="w-28 bg-bg-surface border border-border rounded-lg px-2 py-1.5 text-text-primary text-xs text-right outline-none focus:border-cyan"
                          value={exEditing[key] ?? ''}
                          onChange={e => setExEditing(p => ({ ...p, [key]: e.target.value }))}
                        />
                        <Button size="sm" loading={exSaving[key]} disabled={String(exEditing[key]) === String(entry?.value ?? '')} onClick={async () => {
                          setExSaving(p => ({ ...p, [key]: true }));
                          try {
                            await exchangeAPI.updateSetting(key, exEditing[key]);
                            toast.success(key + ' updated');
                            fetchExSettings();
                          } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
                          finally { setExSaving(p => ({ ...p, [key]: false })); }
                        }}>Save</Button>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Sell Settings */}
              <Card>
                <SectionHeader title="USDT → UGX Sell Settings" />
                <p className="text-text-muted text-xs mb-4">The sell spread is DWI FINTECH trading margin. No additional fees on sell.</p>
                <div className="space-y-3">
                  {[
                    { key: 'EXCHANGE_SELL_SPREAD',    label: 'Sell Spread (fraction)',   hint: '0.02 = 2%' },
                    { key: 'EXCHANGE_MIN_USDT_SELL',  label: 'Minimum Sell (USDT)',      hint: 'e.g. 1' },
                    { key: 'EXCHANGE_MAX_USDT_SELL',  label: 'Maximum Sell (USDT)',      hint: 'e.g. 100000' },
                  ].map(({ key, label, hint }) => {
                    const entry = exSettings.find(e => e.key === key);
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-text-primary text-xs font-semibold">{label}</p>
                          <p className="text-text-muted text-[10px]">{hint}</p>
                        </div>
                        <input
                          className="w-28 bg-bg-surface border border-border rounded-lg px-2 py-1.5 text-text-primary text-xs text-right outline-none focus:border-cyan"
                          value={exEditing[key] ?? ''}
                          onChange={e => setExEditing(p => ({ ...p, [key]: e.target.value }))}
                        />
                        <Button size="sm" loading={exSaving[key]} disabled={String(exEditing[key]) === String(entry?.value ?? '')} onClick={async () => {
                          setExSaving(p => ({ ...p, [key]: true }));
                          try {
                            await exchangeAPI.updateSetting(key, exEditing[key]);
                            toast.success(key + ' updated');
                            fetchExSettings();
                          } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
                          finally { setExSaving(p => ({ ...p, [key]: false })); }
                        }}>Save</Button>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* USDT Withdrawal Fee */}
              <Card>
                <SectionHeader title="USDT External Withdrawal Fees" />
                <p className="text-text-muted text-xs mb-4">Charged when USDT leaves DWI to an external wallet. Service fee + blockchain network fee.</p>
                <div className="space-y-3">
                  {[
                    { key: 'USDT_WITHDRAWAL_SERVICE_FEE_PCT', label: 'DWI Service Fee (%)',   hint: '3.5 = 3.5%' },
                    { key: 'CRYPTO_WITHDRAW_FEE_BEP20',       label: 'BEP20 Network Fee (USDT)', hint: 'Blockchain cost' },
                    { key: 'CRYPTO_WITHDRAW_FEE_TRC20',       label: 'TRC20 Network Fee (USDT)', hint: 'Blockchain cost' },
                    { key: 'CRYPTO_WITHDRAW_FEE_ERC20',       label: 'ERC20 Network Fee (USDT)', hint: 'Blockchain cost' },
                    { key: 'CRYPTO_WITHDRAW_MIN_USDT',        label: 'Minimum Withdrawal (USDT)', hint: 'e.g. 5' },
                    { key: 'CRYPTO_WITHDRAW_MAX_USDT',        label: 'Maximum Withdrawal (USDT)', hint: 'e.g. 10000' },
                  ].map(({ key, label, hint }) => {
                    const entry = exSettings.find(e => e.key === key);
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-text-primary text-xs font-semibold">{label}</p>
                          <p className="text-text-muted text-[10px]">{hint}</p>
                        </div>
                        <input
                          className="w-28 bg-bg-surface border border-border rounded-lg px-2 py-1.5 text-text-primary text-xs text-right outline-none focus:border-cyan"
                          value={exEditing[key] ?? ''}
                          onChange={e => setExEditing(p => ({ ...p, [key]: e.target.value }))}
                        />
                        <Button size="sm" loading={exSaving[key]} disabled={String(exEditing[key]) === String(entry?.value ?? '')} onClick={async () => {
                          setExSaving(p => ({ ...p, [key]: true }));
                          try {
                            await exchangeAPI.updateSetting(key, exEditing[key]);
                            toast.success(key + ' updated');
                            fetchExSettings();
                          } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
                          finally { setExSaving(p => ({ ...p, [key]: false })); }
                        }}>Save</Button>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Price Source */}
              <Card>
                <SectionHeader title="Price Source" />
                <p className="text-text-muted text-xs mb-4">Live CoinGecko data or manual override. Used for all buy/sell rate calculations.</p>
                <div className="space-y-3">
                  {[
                    { key: 'EXCHANGE_PRICE_SOURCE',      label: 'Price Source',          hint: 'coingecko | config' },
                    { key: 'EXCHANGE_UGX_PER_USDT',      label: 'Manual Rate (UGX/USDT)', hint: 'Used when source=config' },
                    { key: 'EXCHANGE_PRICE_CACHE_SECS',  label: 'Cache TTL (seconds)',   hint: 'How long to cache price' },
                  ].map(({ key, label, hint }) => {
                    const entry = exSettings.find(e => e.key === key);
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-text-primary text-xs font-semibold">{label}</p>
                          <p className="text-text-muted text-[10px]">{hint}</p>
                        </div>
                        <input
                          className="w-28 bg-bg-surface border border-border rounded-lg px-2 py-1.5 text-text-primary text-xs text-right outline-none focus:border-cyan"
                          value={exEditing[key] ?? ''}
                          onChange={e => setExEditing(p => ({ ...p, [key]: e.target.value }))}
                        />
                        <Button size="sm" loading={exSaving[key]} disabled={String(exEditing[key]) === String(entry?.value ?? '')} onClick={async () => {
                          setExSaving(p => ({ ...p, [key]: true }));
                          try {
                            await exchangeAPI.updateSetting(key, exEditing[key]);
                            toast.success(key + ' updated');
                            fetchExSettings();
                          } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
                          finally { setExSaving(p => ({ ...p, [key]: false })); }
                        }}>Save</Button>
                      </div>
                    );
                  })}
                </div>
              </Card>

            </div>
          )}
        </div>
      )}

      {/* CRYPTO NETWORK MODAL */}
      <Modal open={cryptoNetModal} onClose={() => setCryptoNetModal(false)} title={cryptoNetEdit ? 'Edit Network' : 'Add Network'} size="md">
        <div className="space-y-3">
          {!cryptoNetEdit && <Input label="Network Code (e.g. TRC20)" value={cryptoNetForm.code} onChange={e => setCryptoNetForm(p => ({...p, code: e.target.value.toUpperCase()}))} placeholder="TRC20" />}
          <Input label="Network Name" value={cryptoNetForm.name} onChange={e => setCryptoNetForm(p => ({...p, name: e.target.value}))} placeholder="Tron TRC-20" />
          <Input label="Currency" value={cryptoNetForm.currency} onChange={e => setCryptoNetForm(p => ({...p, currency: e.target.value.toUpperCase()}))} placeholder="USDT" />
          <Input label="Symbol / Ticker" value={cryptoNetForm.symbol} onChange={e => setCryptoNetForm(p => ({...p, symbol: e.target.value}))} placeholder="USDT" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Min Deposit" type="number" value={cryptoNetForm.minDeposit} onChange={e => setCryptoNetForm(p => ({...p, minDeposit: e.target.value}))} />
            <Input label="Deposit Fee" type="number" value={cryptoNetForm.depositFee} onChange={e => setCryptoNetForm(p => ({...p, depositFee: e.target.value}))} />
          </div>
          <Input label="Explorer TX URL (optional)" value={cryptoNetForm.explorerTxUrl} onChange={e => setCryptoNetForm(p => ({...p, explorerTxUrl: e.target.value}))} placeholder="https://tronscan.org/#/transaction/{txid}" />
          <Input label="Notes (optional)" value={cryptoNetForm.notes} onChange={e => setCryptoNetForm(p => ({...p, notes: e.target.value}))} />
          <Button className="w-full" onClick={async () => {
            try {
              if (cryptoNetEdit) {
                await cryptoNetworksAPI.update(cryptoNetEdit._id, cryptoNetForm);
                toast.success('Network updated');
              } else {
                await cryptoNetworksAPI.create(cryptoNetForm);
                toast.success('Network created');
              }
              setCryptoNetModal(false);
              fetchCryptoNets();
            } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
          }}>Save</Button>
        </div>
      </Modal>

      {/* ADD WALLET ADDRESS MODAL */}
      <Modal open={cryptoWalletModal} onClose={() => setCryptoWalletModal(false)} title={`Add Wallet — ${cryptoWalletNet?.name || ''}`} size="sm">
        <div className="space-y-3">
          <Input label="Wallet Address" value={cryptoWalletForm.address} onChange={e => setCryptoWalletForm(p => ({...p, address: e.target.value}))} placeholder="0x... or T..." />
          <Input label="Label" value={cryptoWalletForm.label} onChange={e => setCryptoWalletForm(p => ({...p, label: e.target.value}))} placeholder="Primary Wallet" />
          <Select label="Wallet Type" value={cryptoWalletForm.walletType} onChange={e => setCryptoWalletForm(p => ({...p, walletType: e.target.value}))}>
            <option value="hot">Hot Wallet</option>
            <option value="cold">Cold Wallet</option>
            <option value="exchange">Exchange</option>
          </Select>
          <Button className="w-full" onClick={async () => {
            if (!cryptoWalletForm.address.trim()) { toast.error('Enter wallet address'); return; }
            try {
              await cryptoNetworksAPI.addWallet(cryptoWalletNet.code, cryptoWalletForm);
              toast.success('Wallet address added');
              setCryptoWalletModal(false);
              fetchCryptoNets();
            } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
          }}>Add Wallet</Button>
        </div>
      </Modal>

      {/* DEPOSIT REVIEW MODAL */}
      <Modal open={cryptoDepModal} onClose={() => setCryptoDepModal(false)} title={`Review Deposit — ${cryptoDepDetail?.requestId || ''}`} size="md">
        {cryptoDepDetail && (
          <div className="space-y-4">
            <div className="bg-bg-surface rounded-xl p-3 space-y-1">
              <div className="flex justify-between"><span className="text-text-muted text-xs">Network</span><span className="text-text-primary text-xs font-semibold">{cryptoDepDetail.networkName}</span></div>
              <div className="flex justify-between"><span className="text-text-muted text-xs">Expected Amount</span><span className="text-cyan text-xs font-semibold">{cryptoDepDetail.expectedAmount || 'Not specified'} {cryptoDepDetail.currency}</span></div>
              <div className="flex justify-between"><span className="text-text-muted text-xs">Status</span><Badge variant={cryptoDepDetail.status === 'confirmed' ? 'success' : cryptoDepDetail.status === 'rejected' ? 'danger' : 'warning'}>{cryptoDepDetail.status}</Badge></div>
              <div className="flex flex-col gap-1 mt-2"><span className="text-text-muted text-xs">TXID</span><span className="text-cyan text-xs font-mono break-all">{cryptoDepDetail.txid || 'Not submitted'}</span></div>
              {cryptoDepDetail.explorerTxUrl && cryptoDepDetail.txid && (
                <a href={cryptoDepDetail.explorerTxUrl.replace('{txid}', cryptoDepDetail.txid)} target="_blank" rel="noreferrer" className="text-cyan text-xs underline">View on Explorer</a>
              )}
            </div>
            {(cryptoDepDetail.status === 'awaiting_verification' || cryptoDepDetail.status === 'pending') && (
              <>
                <div className="space-y-2">
                  <p className="text-text-primary text-sm font-semibold">Approve & Credit</p>
                  <Input label="Credit Amount (USDT)" type="number" value={cryptoDepCreditAmount} onChange={e => setCryptoDepCreditAmount(e.target.value)} placeholder={cryptoDepDetail.expectedAmount ? String(cryptoDepDetail.expectedAmount) : ''} />
                  <Button className="w-full" style={{ background: '#22c55e', color: 'white' }} loading={cryptoActionLoading} onClick={async () => {
                    if (!cryptoDepCreditAmount) { toast.error('Enter credit amount'); return; }
                    setCryptoActionLoading(true);
                    try {
                      await cryptoDepositAPI.approve(cryptoDepDetail._id, { creditAmount: parseFloat(cryptoDepCreditAmount) });
                      toast.success('Deposit approved and wallet credited!');
                      setCryptoDepModal(false);
                      fetchCryptoDeposits();
                    } catch (err) { toast.error(err?.response?.data?.message || 'Approval failed'); }
                    finally { setCryptoActionLoading(false); }
                  }}>Approve & Credit USDT</Button>
                </div>
                <div className="space-y-2">
                  <p className="text-text-primary text-sm font-semibold">Request More Info</p>
                  <Input label="Note to customer" value={cryptoDepMoreInfoNote} onChange={e => setCryptoDepMoreInfoNote(e.target.value)} placeholder="Please provide clearer screenshot..." />
                  <Button className="w-full" loading={cryptoActionLoading} onClick={async () => {
                    setCryptoActionLoading(true);
                    try {
                      await cryptoDepositAPI.requestInfo(cryptoDepDetail._id, { note: cryptoDepMoreInfoNote });
                      toast.success('Customer notified');
                      setCryptoDepModal(false);
                      fetchCryptoDeposits();
                    } catch (err) { toast.error(err?.response?.data?.message || 'Failed'); }
                    finally { setCryptoActionLoading(false); }
                  }}>Request Info</Button>
                </div>
                <div className="space-y-2">
                  <p className="text-red text-sm font-semibold">Reject</p>
                  <Input label="Rejection Reason" value={cryptoDepRejectReason} onChange={e => setCryptoDepRejectReason(e.target.value)} placeholder="Invalid TXID / wrong network..." />
                  <Button className="w-full" style={{ background: '#ef4444', color: 'white' }} loading={cryptoActionLoading} onClick={async () => {
                    if (!cryptoDepRejectReason) { toast.error('Provide a rejection reason'); return; }
                    setCryptoActionLoading(true);
                    try {
                      await cryptoDepositAPI.reject(cryptoDepDetail._id, { reason: cryptoDepRejectReason });
                      toast.success('Deposit rejected');
                      setCryptoDepModal(false);
                      fetchCryptoDeposits();
                    } catch (err) { toast.error(err?.response?.data?.message || 'Reject failed'); }
                    finally { setCryptoActionLoading(false); }
                  }}>Reject Deposit</Button>
                </div>
              </>
            )}
            {(cryptoDepDetail.status === 'confirmed' || cryptoDepDetail.status === 'rejected') && (
              <p className="text-text-muted text-sm text-center">This deposit has already been {cryptoDepDetail.status}.</p>
            )}
          </div>
        )}
      </Modal>

    </div>
  );
}
